import { getDatabase, ref, get, set, update, onValue, runTransaction } from "firebase/database";
import { db } from "./firebase";
import { getCompanion, getSolConverterCost, getStarterCompanionId, isCompanionUnlocked, radiantKey, getRadiantCost, getCompanionShopPrice, COMPANION_SHOP_PENDING_MS } from "./companions";
import { grantAchievement } from "./achievementWrites";
import { equipGrace as kitEquipGrace } from "./kingsGrace"; // KING'S GRACE — the one equip write
import { tallyFactionWin } from "./factionWars"; // H5 — a leaf module; no cycle back into here
import { spendCrests as ieSpendCrests, grantCrests as ieGrantCrests } from "./ieEconomy"; // ◆ the canonical reserve lane, both directions — aliased on purpose
import { getAllGrimoireCards, getAllImpera, CARD_TYPES } from "./cards";
import { STARTER_DECKS } from "./starterDecks";
// ⭐ THE SHELF IS THE PRICE. openPack reads pack.cost off this table
// rather than off progression's own PACK_COSTS — see the banner there.
// storyData.js imports only ./cards, so this is a leaf edge and cannot
// close a cycle back into progression.
import { BOOSTER_PACKS, INNER_VILLAINS } from "./storyData";
import { baseId } from "./necroSeal"; // strip the Necro Seal before any exact-key card lookup
// ESCLING_SLOTS is the width of the battle line and the single source of
// truth for it. A deck set stores one Escling per rift, so the stored array
// has to be exactly this wide — see _emptySet and _normalizeSet below.
import { ESCLING_SLOTS } from "./rules";
import { canonTier } from "./PackArt"; // T21: legacy foil keys (holo_void) price as their canon tier
import {
  NODE_LOOT, lootForNode, MINERIUM1_PUPIL,
  MINERIUM1_SOURCE, MINERIUM1_MYTHIC_HOLO_TIER,
} from "./minerium1Rewards"; // THE BLUEPRINT — pure data, imports only ./cards, so no cycle

// ─────────────────────────────────────────────────────────────
// PROFILE-KEY CHOKEPOINT — the one filter every profile write passes through.
//
// THE WIPE-CLASS BUG: a profile node is only safe when keyed by the Firebase
// Auth uid. If anything ever writes profiles/{displayName} it shadows the
// real profiles/{uid} node, and currency/collection appear to vanish. Under
// the new owner-only rules a name-keyed write also SILENTLY FAILS, so the
// player just loses the grant with no error.
//
// Rather than trust 40+ call sites to always pass a uid, every write-function
// below routes its key through _uidKey(). A Firebase Auth uid is a 28-char
// alphanumeric token. Anything that doesn't look like one (a display name, a
// literal "Aenarchist" fallback, undefined) is REFUSED here — the write never
// happens, so a name-keyed profile node is structurally impossible to create.
//
// This is intentionally strict. A refused write logs loudly and returns the
// function's safe "nothing happened" value. If you ever see this warn, the
// CALLER is passing a name where a uid belongs — fix the caller, not this.
// ─────────────────────────────────────────────────────────────
const _looksLikeUid = (k) =>
  typeof k === "string" && /^[A-Za-z0-9]{20,40}$/.test(k);

const _uidKey = (key, fnName = "profile write") => {
  if (_looksLikeUid(key)) return key;
  console.error(
    `[progression] BLOCKED non-uid profile key in ${fnName}:`,
    JSON.stringify(key),
    "— caller must pass the Firebase Auth uid, not a display name. Write refused."
  );
  return null;
};

// ─────────────────────────────────────────────────────────────
// TERROR — PROGRESSION SYSTEM
// Chocolate Coins, card collection, story progress
//
// ◕ CURRENCY RESTRUCTURE — 2026-07-28 ────────────────────────
// Terror's everyday money is CHOCOLATE COINS (◕ / CC / `chocolateCoins`).
// It was called Divine Crests until this date; same numbers, same shop,
// new name. Terror mints it, Terror writes it, nobody else touches it.
//
// DIVINE CRESTS (◆ / DC / `divineCrests`) are the Empire-wide RESERVE,
// owned by no title. Terror may GRANT them from one-time sources via
// grantCrests(), and never runs a repeatable DC faucet.
//
// ◆ AMENDED 2026-08-18 — THE ENDGAME RULING (caP'n, verbatim in
// substance): *"DC are endgame currency for EVERY game. You can still
// earn some from Terror, but the idea is if you ever get too many, or
// you play another game and don't like their currency, you go to the
// Well in UP and get DC, then go to your game and spend the DC on the
// best endgame gear you can get. And if you don't want any of that,
// exchange one more time for Chocolate Coins and spend it on whatever
// you want. It's up to the player how they spend it — but DC is
// definitely a currency inside not just Terror but every game, as true
// endgame currency."*
//
// SO: the old line "Terror never spends the reserve" is RETIRED. It was
// never the Empire's law — it was a temporary Terror-local hold while
// the plumbing was proven, and it read as doctrine because it sat in a
// doctrine block. The Empire's law has always been the one in
// `ie-cross-game-economy`: **DC is rare and buys ENDGAME ONLY. Not
// bread. Not boosters. The top shelf.** Terror's top shelf is the Foil
// Vault and its mythic cosmetics, and those now cost ◆.
//
// The three laws that come with it, none of them new:
//   • THE MYTHICAL LAW — DC is grindable through the Well, so a
//     DC-bought Mythical is ONE PER ACCOUNT. The key is rare, not the
//     supply, so the supply has to be capped at the door.
//   • THE FAUCET LAW — unchanged. DC still only enters from a source
//     that cannot fire twice, or from the Well.
//   • THE LANE LAW — Terror writes `chocolateCoins` and may grant and
//     now SPEND `divineCrests`. It still writes nobody else's currency.
//
// ⚠ THE WRITE-TRUST GAP IS REAL AND UNCHANGED. Firebase validates
// `divineCrests` as "a non-negative number" and nothing more, so a shop
// reads a balance it cannot believe until server authority ships
// (IE_CURRENCY_HARDENING.md, Layer Two). This is NOT a new risk created
// by this ruling — Terror's ◕ prices have the identical gap and always
// have. The one thing that IS new: forged ◆ spends in every title, not
// just this one. Named here so the hardening pass knows what it is
// protecting, not as a reason to hold the ruling.
//
// ⚠ THE ALIASES ARE GONE (2026-08-18). `awardCrests`, `spendCrests`,
// `CREST_REWARDS` and `earnDepthCrests` were deprecated forwards onto
// the COIN lane, kept so pre-rename call sites compiled. `spendCrests`
// in particular was a live landmine: `ieEconomy.js` exports a function
// with the SAME NAME that spends real ◆, so an import from the wrong
// file silently moved the wrong currency — which is precisely the trap
// `ie-cross-game-economy` warns about ("a deprecated alias must not
// survive a currency repoint"). All four are deleted and their five
// call sites now say what they mean. To spend the reserve, use
// `spendDivineCrests()` below, which forwards to the canonical
// implementation instead of growing a sixth copy of it.
// ─────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────

// ── CONSTANTS ────────────────────────────────────────────────
export const COIN_REWARDS = {
  NPC_WIN: 75,                  // every win (Story Mode)
  FIRST_DEFEAT_BONUS: 100,      // first time defeating each story opponent
  MID_DUNGEON_BONUS: 75,        // after defeating 2nd opponent in a dungeon
  STORY_CHAPTER_CLEAR: 150,     // completing a full dungeon
  STORY_REMATCH: 5,             // re-defeating an already-beaten story opponent ("almost nothing")
  FIRST_WIN_BONUS: 100,         // very first win ever
  DAILY_LOGIN: 25,
  STORY_COMPLETE: 1500,         // beat J'ynxi — the grand finale
  // ── PvP (C8, July 17 2026) — human-vs-human Solrends finally pay.
  // Kept deliberately modest: PvP repeats infinitely between friends,
  // so the crest faucet must not out-earn Story (75) or make win-trading
  // a farm. 5◆ per win ≈ a slow honest trickle; 1◆ loss = participation.
  PVP_WIN: 5,
  PVP_LOSS: 1,
};

// @deprecated — pre-2026-07-28 name. Terror's pocket money is Chocolate
// Coins now; the numbers never moved. Kept so older call sites compile.
// CREST_REWARDS was an alias of COIN_REWARDS and its name lied — these
// are Chocolate Coin payouts. Removed 2026-08-18; call sites now import
// COIN_REWARDS. (See THE ALIASES ARE GONE, above.)

// ── PVP LADDER SCORE (C8) ─────────────────────────────────────
// The simple pre-season ladder: flat deltas, floor 0, peak tracked.
// This is the scaffold RANKED SEASONS (M1) graduates from — when M1
// arrives, placement matches seed from `pvp/score` and these constants
// become the season's K-values.
export const PVP_SCORE = {
  WIN:  20,
  LOSS: -12,
};

// ── ENDLESS DEPTHS DC REWARDS ──────────────────────────────────
// Small DC per win, scaled by current Elo rank
// Gatekeeper victories pay 3× the rank reward
// Loss = 0, Draw = 1 flat
export const DEPTH_COIN_REWARDS = {
  escling:    1,   // 0–399 Elo
  beb:        2,   // 400–799
  champion:   3,   // 800–1199
  lord:       4,   // 1200–1699
  escendant:  5,   // 1700–2199
  aenarchist: 6,   // 2200–2799
  solrend:    8,   // 2800+
  draw:       1,   // any rank, draw
  gatekeeperMult: 3, // multiply win reward for gatekeeper fights
};

// @deprecated — Endless Depths pays Chocolate Coins, not Divine Crests.
export const DEPTH_DC_REWARDS = DEPTH_COIN_REWARDS;

// ⚠ NOT THE PRICE ANY MORE. openPack reads BOOSTER_PACKS[].cost — the
// number the SHELF shows the player — and falls back here only for a
// pack id that predates that table. Kept rather than deleted because
// these are the ORIGINAL tuned numbers and the shelf is at 1 by decree
// until launch; when real prices are restored, restore them into
// storyData.js's BOOSTER_PACKS and treat this as the reference sheet.
// Do not re-point openPack at it. Two price tables is how this broke.
export const PACK_COSTS = {
  // ── Core Archetypes (5 DC each)
  void_eclipse:        5,
  solar_ascension:     5,
  clockwork_dominion:  5,
  dragons_wrath:       5,
  supreme_council:     5,
  instinct_rising:     5,
  unstable_world:      5,
  
  // ── Holographic Boutique — Guaranteed Foil (12-16 DC each)
  void_eclipse_mythic: 12,
  solar_ascension_gold: 12,
  clockwork_dominion_depths: 15,
  dragons_wrath_solrend: 14,
  supreme_council_aeterni: 16,
  instinct_rising_shadow: 13,
  void_eclipse_void: 13,
  unstable_world_jynxi: 14,
  
  // ── Mono-School Power (6 DC each)
  space_ascendants:    6,
  phantom_mastery:     6,
  time_engine:         6,
  dragons_armory:      6,
  
  // ── Tome Mastery (7 DC each)
  shadow_gambler:      7,
  rituals_ascent:      7,
  nihils_embrace:      7,
  contract_broker:     7,
  architects_codex:    7,
  treasure_hoard:      7,
  omnias_dominion:     7,
  ancient_sorcery:     7,
  
  // ── Premier Experiences (8-20 DC)
  all_stars_mythic:    20,
  depths_sanctuary:    18,
  hybrid_chaos:        9,
  affinity_ascension:  8,
  prophecy_oracle:     9,
  
  // ── The Testing Playground
  randomizer_supreme:  4,
  
  // ── Special (free)
  special:             0,
};

export const PACK_CONTENTS = {
  // 10 cards: 3 common, 2 uncommon, 4 rare, 1 holo slot (15% chance)
  standard: { total: 10, commons: 3, uncommons: 2, rares: 4, holoChance: 0.15 },
  // 12 cards: guaranteed 2 holos — drops from dungeon bosses
  special:  { total: 12, commons: 3, uncommons: 3, rares: 4, holoChance: 1.0, guaranteedHolos: 2 },
  
  // ── HOLOGRAPHIC BOUTIQUE — Guaranteed holos ──────────────────
  // Every card pulls as holographic
  holo_guaranteed: { total: 10, commons: 3, uncommons: 2, rares: 4, holoChance: 1.0 },

  // ── THE CORE DRAWERS, 2026-08-22 ─────────────────────────────
  // ⭐ caP'n: *"Class packs are fine for now. Tome Packs, they come in
  // packs of 3. Support cards are packs of 4."*
  //
  // A ten-card Tome pack was dealing more than a tenth of an entire
  // school in one purchase — nine Tomes per ilk, so a single pack could
  // hand you the school. Three is a pack you buy repeatedly; ten was a
  // pack you buy once. Same argument at a different size for Support.
  //
  // ⚠ `total` IS LOAD-BEARING — see THE TENTH CARD below. The draw
  // loops deal commons + uncommons + rares and then derive the
  // remaining slots from `total`, so these three numbers must sum to
  // `total` exactly or the difference silently becomes extra rares.
  //   tome_pack     2 + 0 + 1 = 3  ✓
  //   support_pack  2 + 1 + 1 = 4  ✓
  // School packs are deliberately absent: they are `standard`, unchanged.
  tome_pack:    { total: 3, commons: 2, uncommons: 0, rares: 1, holoChance: 0.15 },
  support_pack: { total: 4, commons: 2, uncommons: 1, rares: 1, holoChance: 0.15 },
};

// ═══════════════════════════════════════════════════════════════
// THE CORE — ONE TABLE, AND EVERYTHING READS IT
// ═══════════════════════════════════════════════════════════════
// ⭐ caP'n, 2026-08-22: *"the Class, Tomes, and Support [shelves] will
// be bundled into a tab you must first click on called 'Core' to expand
// and see the 3 options ... that way they will always guaranteed be
// cores even when I make new cards and we make new booster packs, they
// will still be 'Core' you see?"*
//
// ── WHY THE TABLE LIVES HERE AND NOT IN THE SHOP ─────────────────
// "Core" is now the answer to THREE separate questions that used to be
// answered in three different files:
//   1. WHICH MARK does a card out of this pack wear?   (PACK_SOURCE)
//   2. HOW MANY CARDS does this pack deal?             (PACK_CONTENTS)
//   3. WHICH DRAWER does this pack sit in on the shelf? (CATEGORY_DEFS)
// Three lists of the same thirteen pack ids is the PACK_COSTS bug with
// extra steps — the first pack added to a drawer would have gone into
// two of them and been silently wrong in the third. So there is ONE
// list, it is here beside the pull that has to trust it, and the shop
// imports it rather than retyping it.
//
// ⭐ ADDING A NEW CORE PACK IS ONE LINE — an id in the right drawer.
// It then arrives already wearing the Core sigil, already dealing its
// drawer's card count, and already on the right shelf under the Core
// tab. Adding a whole new DRAWER is one row, and it gets all three too.
export const CORE_DRAWERS = {
  monoschool: {
    label: "◈ School", name: "Mono-School Power", subtitle: "Dedicated Pursuit",
    contents: "standard",     // 10 cards — "Class packs are fine for now"
    venus: '"One family, all the way down. People who know what they want buy these."',
    ids: ["space_ascendants", "phantom_mastery", "time_engine", "dragons_armory"],
  },
  tome: {
    label: "◈ Tomes", name: "Tome Mastery", subtitle: "Spellcraft Focus",
    contents: "tome_pack",    // 3 cards
    venus: '"Spellcraft. Read them before you play them, is my advice."',
    // EIGHT, one per Tome ilk — cards.js is the count: eight ilks, nine
    // Tomes each. Four of the eight schools had no pack at all until
    // 2026-08-22, which is why Omnia was unbuyable while Nihil was not.
    ids: ["shadow_gambler", "rituals_ascent", "nihils_embrace", "contract_broker",
          "architects_codex", "treasure_hoard", "omnias_dominion", "ancient_sorcery"],
  },
  support: {
    label: "◈ Support", name: "Support", subtitle: "What you bring them FOR",
    contents: "support_pack", // 4 cards
    venus: '"The little ones and the loud ones. Nobody comes in for these and everybody leaves with one."',
    ids: ["support_relics"],
  },
};

// packId → drawer key. Built, never typed.
export const CORE_DRAWER_OF = Object.fromEntries(
  Object.entries(CORE_DRAWERS).flatMap(([key, d]) => d.ids.map(id => [id, key]))
);

// "Is this pack part of the Core?" — the one predicate. The Core sigil,
// the Core tab and the drawer card counts all hang off this answer.
export const isCorePack = (packId) => !!CORE_DRAWER_OF[packId];

// ── getPackContents — how many cards, and of what, for ANY pack ──
// ⚠ This is the ONLY place the shop and the pull are allowed to learn a
// pack's size. Both printed a hardcoded "10 cards per pack" before, in
// two places, and the pull read PACK_CONTENTS.standard directly — three
// independent claims about one number, which is exactly how a shelf
// ends up advertising ten and dealing three.
export const getPackContents = (packType, { boutique = false, special = false } = {}) => {
  if (special)  return PACK_CONTENTS.special;
  if (boutique) return PACK_CONTENTS.holo_guaranteed;
  const drawer = CORE_DRAWERS[CORE_DRAWER_OF[packType]];
  return PACK_CONTENTS[drawer?.contents || "standard"] || PACK_CONTENTS.standard;
};

// Rarity tiers — used for pack pulls
export const RARITY = {
  COMMON: "common",
  RARE: "rare",
  HOLO_SILVER: "holo_silver",  // ◈ Silver — base holo, silver shimmer
  HOLO_GOLD: "holo_gold",      // ✦ Gold — premium holo, gold shimmer
  HOLO_MYTHIC: "holo_mythic", // ◆ Mythic — rarest, full color foil
  HOLO_VOID: "holo_void",      // ◈ Void — deep purple pulse
  HOLO_SOLREND: "holo_solrend", // ◆ Solrend — crimson fire
  HOLO_AETERNUS: "holo_aeternus", // ✦ A'eterni — celestial white
  HOLO_SHADOW: "holo_shadow",   // ◇ Shadow — invert foil
  HOLO_DEPTHS: "holo_depths",   // ◆ Depths — ocean cascade
  HOLO_JYNXI: "holo_jynxi",     // ✦ J'ynxi — trickster emerald green
  // Legacy alias
  HOLO: "holo_silver",
};

// Holo tier chances per pack slot (standard distribution)
const rollHoloTier = () => {
  const r = Math.random();
  if (r < 0.05) return RARITY.HOLO_MYTHIC;  // 5% — rarest
  if (r < 0.20) return RARITY.HOLO_GOLD;     // 15% — gold
  return RARITY.HOLO_SILVER;                  // 80% of holos — silver
};

// Extended holo tier roller — for boutique packs
const rollExtendedHoloTier = (tierList) => {
  // tierList: array of holo tier strings (e.g., ["holo_mythic", "holo_silver"])
  // Returns one of the specified tiers with equal weight
  if (!tierList || tierList.length === 0) return RARITY.HOLO_SILVER;
  return tierList[Math.floor(Math.random() * tierList.length)];
};

// Which cards belong to which pack theme
export const PACK_POOLS = {
  // ── Void Eclipse (VE): Phantom beings + Nihil + Ritual Tomes ──
  void_eclipse: {
    common: [
      "spectral_tiger_beb", "shadow_fox_beb", "inverted_serpent_beb",
      "escling_phantom",
      "tome_khao_garden", "tome_bhodi_tree", "tome_khao_duhmdumn_beb",
      "tome_nothing_left", "tome_not_here", "tome_not_now", "tome_not_ever",
      "tome_wall_of_light", "tome_seal_of_nihil", "tome_rays_of_hope",
      "tome_shower_of_lyt", "tome_soulcerers_seal",
    ],
    rare: [
      "spectral_tiger_champion", "spectral_tiger_lord",
      "shadow_fox_champion", "shadow_fox_lord",
      "inverted_serpent_champion", "inverted_serpent_lord",
      "p_spectral_tiger_1", "p_spectral_tiger_2", "p_spectral_tiger_3",
      "p_shadow_fox_1", "p_shadow_fox_2", "p_shadow_fox_3",
      "p_inverted_serpent_1", "p_inverted_serpent_2", "p_inverted_serpent_3",
      "u_spectral_tiger_lord", "u_shadow_fox_lord", "u_inverted_serpent_lord",
      "tome_monoliths_of_nihil", "tome_mausoleum_of_nihil",
      "tome_sword_shield_nihil", "tome_soulcerers_monoliths", "tome_soulcerers_mausoleum",
      "tome_shadow_blanket", "tome_inverted_knife", "tome_light_casting_shadow",
      "tome_river_of_blood", "tome_blood_in_blood_out", "tome_blood_money",
      "aff_evil", "proph_phantom_planet",
    ],
  },

  // ── Solar Ascension (SA): Space beings + Construction + Treasure Tomes ──
  solar_ascension: {
    common: [
      "h01_beb", "h09_beb", "xulan_beb",
      "escling_space",
      "tome_khao_garden", "tome_bhodi_tree", "tome_khao_duhmdumn_beb",
      "tome_nothing_left", "tome_not_here", "tome_wall_of_light",
      "tome_stars_and_skies", "tome_anima_house", "tome_gauntlets_azka",
    ],
    rare: [
      "h01_champion", "h01_lord",
      "h09_champion", "h09_lord",
      "xulan_champion", "xulan_lord",
      "p_h01_1", "p_h01_2", "p_h01_3",
      "p_h09_1", "p_h09_2", "p_h09_3",
      "p_xulan_1", "p_xulan_2", "p_xulan_3",
      "u_h01_lord", "u_h09_lord", "u_xulan_lord",
      "tome_khao_arcadia", "tome_khao_paradise",
      "tome_boots_wing_walking", "tome_lyt_footed",
      "tome_necklace_death", "tome_necklace_courage", "tome_necklace_akula",
      "tome_chief_calidum", "tome_lightning_blade",
      "aff_holy", "proph_space_race",
    ],
  },

  // ── Clockwork Dominion (CD): Time beings + Omnia + Contract Tomes ──
  clockwork_dominion: {
    common: [
      "z4z_beb", "dg0_beb", "antdroid_beb",
      "escling_time",
      "tome_khao_garden", "tome_bhodi_tree", "tome_khao_duhmdumn_beb",
      "tome_nothing_left", "tome_not_here", "tome_wall_of_light",
      "tome_seal_of_omnis", "tome_soulcerers_seal",
      "tome_agree_to_disagree", "tome_restraining_ordeal",
    ],
    rare: [
      "z4z_champion", "z4z_lord",
      "dg0_champion", "dg0_lord",
      "antdroid_champion", "antdroid_lord",
      "p_z4z_1", "p_z4z_2", "p_z4z_3",
      "p_dg0_1", "p_dg0_2", "p_dg0_3",
      "p_antdroid_1", "p_antdroid_2", "p_antdroid_3",
      "u_z4z_lord", "u_dg0_lord", "u_antdroid_lord",
      "tome_monoliths_of_omnis", "tome_mausoleum_of_omnis",
      "tome_sword_shield_omnis", "tome_omnis_corruption",
      "tome_devils_advocate", "tome_judge_jury", "tome_solomon_godfry",
      "tome_regnant", "tome_superque", "tome_all_or_nothing",
      "aff_awoken", "proph_time_to_pretend",
    ],
  },

  // ── Dragon's Wrath (DW): Dragon beings + Ancient Sorcery + Shadow Gambling ──
  dragons_wrath: {
    common: [
      "lucky_dragon_beb", "premier_dragon_beb", "chaos_dragon_beb",
      "escling_dragon",
      "tome_khao_garden", "tome_bhodi_tree", "tome_khao_duhmdumn_beb",
      "tome_nothing_left", "tome_not_here", "tome_wall_of_light",
      "tome_temptation_manifested", "tome_snake_eyes",
    ],
    rare: [
      "lucky_dragon_champion", "lucky_dragon_lord",
      "premier_dragon_champion", "premier_dragon_lord",
      "chaos_dragon_champion", "chaos_dragon_lord",
      "p_lucky_dragon_1", "p_lucky_dragon_2", "p_lucky_dragon_3",
      "p_premier_dragon_1", "p_premier_dragon_2", "p_premier_dragon_3",
      "p_chaos_dragon_1", "p_chaos_dragon_2", "p_chaos_dragon_3",
      "u_lucky_dragon_lord", "u_premier_dragon_lord", "u_chaos_dragon_lord",
      "tome_sword_of_shadows", "tome_weeping_dagger", "tome_inverted_knife",
      "tome_casino_royale", "tome_dragon_deyes", "tome_gambits_lucky_cat",
      "tome_nevermore", "tome_underdogs_howl", "tome_daft_fantasy",
      "tome_goodbye_horses", "tome_monkey_suite", "tome_magma_carta",
      "tome_king_of_the_castle", "tome_seize_and_disease",
      "aff_jynxi", "proph_dragonforce",
    ],
  },

  // ── Supreme Council (SC): All U'hltis + Affinities + Prophecies + Curios ──
  supreme_council: {
    common: [
      "curio_wizards_cap", "curio_circle_of_lyf", "curio_jynxi_wares",
      "curio_spectacles", "curio_mages_amulet", "curio_elixir",
      "curio_warlocks_robe", "curio_beb_buddy", "curio_badass_beb",
      "aff_holy", "aff_evil", "aff_awoken", "aff_jynxi",
    ],
    rare: [
      "u_h01_lord", "u_h09_lord", "u_xulan_lord",
      "u_spectral_tiger_lord", "u_shadow_fox_lord", "u_inverted_serpent_lord",
      "u_z4z_lord", "u_dg0_lord", "u_antdroid_lord",
      "u_lucky_dragon_lord", "u_premier_dragon_lord", "u_chaos_dragon_lord",
      "proph_space_race", "proph_phantom_planet", "proph_dragonforce",
      "proph_time_to_pretend", "proph_tablet_omnis", "proph_tablet_nullus",
      "proph_ace_of_shades", "proph_bloody_hell",
      "tome_divine_justice", "tome_cleansing_burst", "tome_phoenix_aura",
      "tome_radiant_aura", "tome_radiant_armor", "tome_wall_of_life",
      "tome_lyt_hearted",
    ],
  },

  // ── Instinct Rising (IR): All 36 Impera + Curios ──
  instinct_rising: {
    common: [
      "p_h01_1", "p_h09_1", "p_xulan_1",
      "p_spectral_tiger_1", "p_shadow_fox_1", "p_inverted_serpent_1",
      "p_z4z_1", "p_dg0_1", "p_antdroid_1",
      "p_lucky_dragon_1", "p_premier_dragon_1", "p_chaos_dragon_1",
      "curio_wizards_cap", "curio_circle_of_lyf", "curio_spectacles",
      "curio_mages_amulet", "curio_elixir", "curio_beb_buddy",
    ],
    rare: [
      "p_h01_2", "p_h01_3",
      "p_h09_2", "p_h09_3",
      "p_xulan_2", "p_xulan_3",
      "p_spectral_tiger_2", "p_spectral_tiger_3",
      "p_shadow_fox_2", "p_shadow_fox_3",
      "p_inverted_serpent_2", "p_inverted_serpent_3",
      "p_z4z_2", "p_z4z_3",
      "p_dg0_2", "p_dg0_3",
      "p_antdroid_2", "p_antdroid_3",
      "p_lucky_dragon_2", "p_lucky_dragon_3",
      "p_premier_dragon_2", "p_premier_dragon_3",
      "p_chaos_dragon_2", "p_chaos_dragon_3",
      "curio_jynxi_wares", "curio_warlocks_robe", "curio_badass_beb",
      "proph_bloody_hell",
      "tome_khao_duhmdumn_champion", "tome_khao_duhmdumn_lord",
    ],
  },

  // ── Unstable World (UW): All L1 beings + U'hltis + Shadow Gambling + Affinities + Esclings ──
  unstable_world: {
    common: [
      "h01_beb", "h09_beb", "xulan_beb",
      "spectral_tiger_beb", "shadow_fox_beb", "inverted_serpent_beb",
      "z4z_beb", "dg0_beb", "antdroid_beb",
      "lucky_dragon_beb", "premier_dragon_beb", "chaos_dragon_beb",
      "escling_space", "escling_phantom", "escling_time", "escling_dragon",
      "aff_holy", "aff_evil", "aff_awoken", "aff_jynxi",
      "tome_temptation_manifested", "tome_snake_eyes",
      "tome_devils_advocate", "tome_agree_to_disagree",
      "tome_khao_garden", "tome_khao_duhmdumn_beb",
    ],
    rare: [
      "u_h01_lord", "u_h09_lord", "u_xulan_lord",
      "u_spectral_tiger_lord", "u_shadow_fox_lord", "u_inverted_serpent_lord",
      "u_z4z_lord", "u_dg0_lord", "u_antdroid_lord",
      "u_lucky_dragon_lord", "u_premier_dragon_lord", "u_chaos_dragon_lord",
      "tome_casino_royale", "tome_dragon_deyes", "tome_gambits_lucky_cat",
      "tome_nevermore", "tome_daft_fantasy", "tome_goodbye_horses",
      "tome_monkey_suite", "tome_king_of_the_castle",
      "proph_ace_of_shades", "proph_tablet_omnis", "proph_tablet_nullus",
      "curio_badass_beb", "curio_beb_buddy", "curio_jynxi_wares",
    ],
  },


  // ══════════════════════════════════════════════════════════════
  // THE EIGHT TOME-SCHOOL POOLS — one ilk each, all nine of it
  // ══════════════════════════════════════════════════════════════
  // ⭐ caP'n, 2026-08-22: *"the shadow gambling pack says all 8 shadow
  // gambling, every single School (Tome Art) has 9"* — and the count
  // was the smaller half of the problem. Every Tome pack was ALIASED
  // onto a core being-pool in PACK_ID_MAP (shadow_gambler →
  // dragons_wrath, nihils_embrace → void_eclipse, and so on), so a
  // pack advertising "All 9 Shadow Gambling Tomes" was dealing Dragon
  // beings, Impera and U'hltis, and the nine tomes it named were a
  // minority of its own pool. The subtitle was not slightly wrong; the
  // pack was not the pack.
  //
  // ⚠ THE ALIAS ROWS FOR ALL EIGHT ARE GONE FROM PACK_ID_MAP. A Tome
  // pack now resolves to its OWN pool and contains its OWN ilk and
  // nothing else. Re-adding an alias for one of these silently turns a
  // school pack back into a being pack, and nothing will report it.
  //
  // THE SPLIT IS THE LEVEL — L1/L2 common, L3 rare, read straight off
  // cards.js. Shadow Gambling is the one exception: all nine of it are
  // Level 3, so its four marquee names take the rare lane.

  // CONSTRUCTION — the board before the fight.
  architects_codex: {
    common: ["tome_khao_garden", "tome_bhodi_tree", "tome_khao_duhmdumn_beb",
             "tome_khao_arcadia", "tome_anima_house", "tome_khao_duhmdumn_champion"],
    rare:   ["tome_khao_paradise", "tome_stars_and_skies", "tome_khao_duhmdumn_lord"],
  },
  // TREASURE — what a being carries.
  treasure_hoard: {
    common: ["tome_gauntlets_azka", "tome_lightning_blade", "tome_necklace_death",
             "tome_boots_wing_walking", "tome_necklace_courage", "tome_sword_of_shadows"],
    rare:   ["tome_chief_calidum", "tome_necklace_akula", "tome_weeping_dagger"],
  },
  // NIHIL — the cleansing light.
  nihils_embrace: {
    common: ["tome_rays_of_hope", "tome_shower_of_lyt", "tome_wall_of_light",
             "tome_cleansing_burst", "tome_lyt_footed", "tome_radiant_aura"],
    rare:   ["tome_divine_justice", "tome_phoenix_aura", "tome_sword_shield_nihil"],
  },
  // OMNIA — the counterweight. Had no pack at all until today.
  omnias_dominion: {
    common: ["tome_nothing_left", "tome_radiant_armor", "tome_wall_of_life",
             "tome_lyt_hearted", "tome_omnis_corruption", "tome_regnant"],
    rare:   ["tome_all_or_nothing", "tome_superque", "tome_sword_shield_omnis"],
  },
  // ANCIENT SORCERY — blood and absence.
  ancient_sorcery: {
    common: ["tome_blood_in_blood_out", "tome_not_here", "tome_shadow_blanket",
             "tome_blood_money", "tome_inverted_knife", "tome_not_now"],
    rare:   ["tome_light_casting_shadow", "tome_not_ever", "tome_river_of_blood"],
  },
  // RITUAL — seals, monoliths, mausoleums.
  rituals_ascent: {
    common: ["tome_seal_of_nihil", "tome_seal_of_omnis", "tome_soulcerers_seal",
             "tome_monoliths_of_nihil", "tome_monoliths_of_omnis", "tome_soulcerers_monoliths"],
    rare:   ["tome_mausoleum_of_nihil", "tome_mausoleum_of_omnis", "tome_soulcerers_mausoleum"],
  },
  // SHADOW GAMBLING — all nine are Level 3; the marquee four take rare.
  shadow_gambler: {
    common: ["tome_daft_fantasy", "tome_goodbye_horses", "tome_nevermore",
             "tome_underdogs_howl", "tome_gambits_lucky_cat"],
    rare:   ["tome_casino_royale", "tome_snake_eyes", "tome_temptation_manifested",
             "tome_dragon_deyes"],
  },
  // CONTRACT — read it before you sign it.
  contract_broker: {
    common: ["tome_devils_advocate", "tome_magma_carta", "tome_restraining_ordeal",
             "tome_agree_to_disagree", "tome_monkey_suite", "tome_seize_and_disease"],
    rare:   ["tome_judge_jury", "tome_king_of_the_castle", "tome_solomon_godfry"],
  },

  // ══════════════════════════════════════════════════════════════
  // THE SUPPORT POOL — everything the shelf forgot
  // ══════════════════════════════════════════════════════════════
  // ⭐ caP'n, 2026-08-22: *"Class and Tomes — does it have U'hltis
  // affinities Curios etc? if not then we should make 1 more section
  // for those and bundle them as Support and throw every other card
  // you forgot in there."*
  //
  // Audited before writing it: the four School packs and the four Tome
  // packs DO reach U'hltis, Affinities and Prophecies (they all alias
  // onto the four core pools, which carry them in `rare`). What they
  // reach NOWHERE is CURIOS — the nine of them appear only in the
  // supreme_council, instinct_rising and unstable_world pools, and
  // every one of those shelves is being deleted. So without this pool
  // the Curios would have become unbuyable the moment the Core and
  // Premier sections came down, and nothing would have said so.
  //
  // So SUPPORT is exactly what caP'n called it — the support cards,
  // and every card that had no other home. It is the ONLY pack in the
  // game whose common lane is Curios, and its rare lane is the twelve
  // U'hltis, the four Affinities and the eight Prophecies: the three
  // card types a deck is BUILT AROUND rather than built out of.
  support_relics: {
    common: [
      "curio_wizards_cap", "curio_circle_of_lyf", "curio_jynxi_wares",
      "curio_spectacles", "curio_mages_amulet", "curio_elixir",
      "curio_warlocks_robe", "curio_beb_buddy", "curio_badass_beb",
      // The four Esclings ride here too. They are the only other thing
      // in the card database with no shelf of its own.
      "escling_space", "escling_phantom", "escling_dragon", "escling_time",
    ],
    rare: [
      "u_h01_lord", "u_h09_lord", "u_xulan_lord",
      "u_spectral_tiger_lord", "u_shadow_fox_lord", "u_inverted_serpent_lord",
      "u_z4z_lord", "u_dg0_lord", "u_antdroid_lord",
      "u_lucky_dragon_lord", "u_premier_dragon_lord", "u_chaos_dragon_lord",
      "aff_holy", "aff_awoken", "aff_evil", "aff_jynxi",
      "proph_space_race", "proph_phantom_planet", "proph_dragonforce",
      "proph_time_to_pretend", "proph_tablet_omnis", "proph_tablet_nullus",
      "proph_ace_of_shades", "proph_bloody_hell",
    ],
  },

  // ══════════════════════════════════════════════════════════════
  // THE ACTIVITY POOLS — one shelf per thing you can go and do
  // ══════════════════════════════════════════════════════════════
  // ⭐ caP'n, 2026-08-22: *"create a booster pack for every single
  // story/activity (this means gauntlet or any other activity rewarded
  // signature foil like SHV etc) so the player can buy them. That
  // being said, SHV and elo depths are purchasable in the Foil Vault
  // yes they should be, however, they should never ever be in booster
  // packs — its either do the activity or buy the specific card you
  // want for an extreme price to bypass the activity, but no booster
  // packs."*
  //
  // ⚠ SO READ THE LINE ABOVE BEFORE EDITING ANY OF THESE. An activity
  // pack sells the activity's CARDS. It must never sell the activity's
  // SIGNATURE FOIL. `BOUTIQUE_PACKS` is what forces a foil tier into a
  // pull, and not one activity pack is in it — that is the enforcement
  // and it is deliberately a hole in a table rather than a flag,
  // because a flag can be set by accident and an absence cannot.
  //
  // The foil is still gettable two ways and only two: do the thing, or
  // pay the Vault an extreme price for the one card you want. That is
  // the whole rarity structure and a pack in the middle of it would
  // dissolve it — caP'n's point about Premier, applied everywhere.

  // MINERIUM ONE — Sir H'osh Vrown's world. His own house (H-09), his
  // own elite (Antdroid), and the Treasure his kingdom was granted
  // with. NOT holo_vrown; that is the Griddle and it is his.
  activity_minerium1: {
    common: [
      "h09_beb", "antdroid_beb", "escling_time", "escling_space",
      "tome_king_of_the_castle", "tome_monkey_suite", "tome_devils_advocate",
      "tome_judge_jury", "tome_casino_royale", "tome_gambits_lucky_cat",
    ],
    rare: [
      "h09_champion", "h09_lord", "antdroid_champion", "antdroid_lord",
      "p_h09_1", "p_h09_2", "p_h09_3", "p_antdroid_1", "p_antdroid_2", "p_antdroid_3",
      "u_h09_lord", "u_antdroid_lord",
      "proph_tablet_omnis", "curio_badass_beb",
    ],
  },

  // THE GAUNTLET — what a ladder rewards: the ones who keep standing.
  // DG-0 shields and Premier Dragons, and the Contract tomes, because
  // a Gauntlet is a agreement you made with yourself. NOT holo_gauntlet.
  activity_gauntlet: {
    common: [
      "dg0_beb", "premier_dragon_beb", "escling_dragon", "escling_time",
      "tome_devils_advocate", "tome_judge_jury", "tome_king_of_the_castle",
      "tome_nevermore", "tome_daft_fantasy",
    ],
    rare: [
      "dg0_champion", "dg0_lord", "premier_dragon_champion", "premier_dragon_lord",
      "p_dg0_1", "p_dg0_2", "p_dg0_3",
      "p_premier_dragon_1", "p_premier_dragon_2", "p_premier_dragon_3",
      "u_dg0_lord", "u_premier_dragon_lord",
      "aff_holy", "proph_dragonforce",
    ],
  },

  // THE ENDLESS DEPTHS — the descent. Phantom things and the water
  // tomes. NOT holo_depths; the Gatekeepers pay that and only they do.
  activity_depths: {
    common: [
      "inverted_serpent_beb", "spectral_tiger_beb", "escling_phantom",
      "tome_nothing_left", "tome_not_here", "tome_not_now", "tome_not_ever",
      "tome_river_of_blood", "tome_shadow_blanket",
    ],
    rare: [
      "inverted_serpent_champion", "inverted_serpent_lord",
      "spectral_tiger_champion", "spectral_tiger_lord",
      "p_inverted_serpent_1", "p_inverted_serpent_2", "p_inverted_serpent_3",
      "p_spectral_tiger_1", "p_spectral_tiger_2", "p_spectral_tiger_3",
      "u_inverted_serpent_lord", "u_spectral_tiger_lord",
      "aff_awoken", "proph_phantom_planet",
    ],
  },

  // THE FACTION WARS — the two halves of the argument. Blades of Omnis
  // and Swords of Nullus, and the Z4Z who fight for both by turns.
  // NOT holo_jynxi; the Hall pays that.
  activity_factions: {
    common: [
      "z4z_beb", "chaos_dragon_beb", "escling_time", "escling_dragon",
      "tome_seal_of_omnis", "tome_monoliths_of_omnis", "tome_mausoleum_of_omnis",
      "tome_seal_of_nihil", "tome_monoliths_of_nihil", "tome_mausoleum_of_nihil",
    ],
    rare: [
      "z4z_champion", "z4z_lord", "chaos_dragon_champion", "chaos_dragon_lord",
      "p_z4z_1", "p_z4z_2", "p_z4z_3",
      "p_chaos_dragon_1", "p_chaos_dragon_2", "p_chaos_dragon_3",
      "u_z4z_lord", "u_chaos_dragon_lord",
      "aff_jynxi", "proph_bloody_hell", "proph_tablet_nullus",
    ],
  },

  // ── Legacy aliases → resolved via resolvePool() ──
  space:   null,
  phantom: null,
  time:    null,
  dragon:  null,
};

// Resolve pool — maps old IDs and new IDs, fallback to unstable_world
const PACK_ID_MAP = {
  // Legacy shortcuts
  space:   "solar_ascension",
  phantom: "void_eclipse",
  time:    "clockwork_dominion",
  dragon:  "dragons_wrath",
  
  // ── Holo Boutique packs route to base pools ──────────────────
  void_eclipse_mythic: "void_eclipse",
  void_eclipse_void: "void_eclipse",
  solar_ascension_gold: "solar_ascension",
  clockwork_dominion_depths: "clockwork_dominion",
  dragons_wrath_solrend: "dragons_wrath",
  supreme_council_aeterni: "supreme_council",
  // ⚠ instinct_rising_shadow removed 2026-08-22 — the pack has been
  // commented out in storyData since the Shadow retirement, so this row
  // routed a pack that cannot be bought. See the banner in
  // BoosterPack's CATEGORY_DEFS.
  unstable_world_jynxi: "unstable_world",
  
  // ── Mono-school packs ──────────────────────────────────────────
  space_ascendants: "solar_ascension",
  phantom_mastery: "void_eclipse",
  time_engine: "clockwork_dominion",
  dragons_armory: "dragons_wrath",
  
  // ── Tome mastery packs ────────────────────────────────────────
  // ⚠ DELIBERATELY EMPTY. All eight Tome packs used to alias onto core
  // being-pools here, which is why "All 9 Shadow Gambling Tomes" dealt
  // Dragon Lords. Each has its OWN pool in PACK_POOLS above now and
  // resolvePool falls through to it.
  
  // ── Premier experiences ───────────────────────────────────────
  all_stars_mythic: "unstable_world",
  depths_sanctuary: "void_eclipse",
  hybrid_chaos: "unstable_world",
  affinity_ascension: "supreme_council",
  prophecy_oracle: "supreme_council",
  
  // ── The testing playground ───────────────────────────────────
  randomizer_supreme: "unstable_world",

  // ⚠ NO ROWS FOR support_relics OR THE activity_* PACKS, ON PURPOSE.
  // resolvePool falls through to PACK_POOLS[id] when this map has no
  // entry, and every one of those packs has a pool of its OWN — which
  // is the entire point of them. Aliasing one onto a core pool would
  // silently turn "the Support pack" into a second Supreme Council,
  // which is exactly the clone problem caP'n deleted the Premier shelf
  // over. If you add an alias here for one of them, you have removed
  // it from the game without removing it from the shelf.
};
const resolvePool = (packType) => {
  const id = PACK_ID_MAP[packType] || packType;
  return PACK_POOLS[id] || PACK_POOLS.unstable_world;
};

// Public resolver — the shop uses this to show exactly which cards a
// pack can contain. Returns { common: [...ids], rare: [...ids] }.
// Works for every pack id (base, boutique, mono, tome, premier, chaos).
export const getPackCardPool = (packType) => {
  const pool = resolvePool(packType);
  return { common: pool.common || [], rare: pool.rare || [] };
};

// Special dungeon pack mascot holographics
export const DUNGEON_HOLO_MASCOTS = {
  dungeon_1: {
    h01: "h01_lord",
    h09: "h09_lord",
    xulan: "xulan_lord",
    spectral_tiger: "spectral_tiger_lord",
    shadow_fox: "shadow_fox_lord",
    inverted_serpent: "inverted_serpent_lord",
    lucky_dragon: "lucky_dragon_lord",
    premier_dragon: "premier_dragon_lord",
    chaos_dragon: "chaos_dragon_lord",
    z4z: "z4z_lord",
    dg0: "dg0_lord",
    antdroid: "antdroid_lord",
  },
};

// ── PLAYER PROFILE ────────────────────────────────────────────
// Key-agnostic profile read. Callers pass the Firebase Auth uid
// (profiles are keyed by uid — see createProfile in Plaza.js).
export const getPlayerProfile = async (uid) => {
  uid = _uidKey(uid, "getPlayerProfile");
  if (!uid) return null;
  const r = ref(db, `profiles/${uid}`);
  const snap = await get(r);
  return snap.exists() ? snap.val() : null;
};

// Key-agnostic live profile listener. Callers pass the uid.
export const listenToProfile = (uid, callback) => {
  const r = ref(db, `profiles/${uid}`);
  return onValue(r, snap => callback(snap.exists() ? snap.val() : null));
};

// ── LEADERBOARD MIRROR ────────────────────────────────────────
// Profiles are owner-private (Firebase rules lock profiles/$uid to
// auth.uid). The Plaza leaderboard needs cross-user reads, so we
// denormalize the two public-safe fields — name + wins — into a
// world-readable /leaderboard/$uid node. Called after any totalWins
// change. Owner-write, world-read (see firebase rules).
// ⭐ EXPORTED 2026-08-20 so the WORLD MAP can use it instead of
// growing a fourth copy. Its own banner below says it is "the ONE
// place in the codebase where 'a Solrend was just won' is already
// true" — and it was called from three of the four places that is
// true, missing the one a story player lives in.
export const mirrorLeaderboard = async (uid, name, wins) => {
  if (!uid) return;
  try {
    await update(ref(db, `leaderboard/${uid}`), {
      name: name || "Aenarchist",
      wins: wins || 0,
      updatedAt: Date.now(),
    });
  } catch (e) {
    // Non-blocking — a leaderboard mirror failure must never break a win.
    console.warn("[mirrorLeaderboard] failed:", e);
  }

  // ── H5 · FACTION WARS ────────────────────────────────────────
  // This function is called from all three sites where a Terror win
  // is recorded — story, free play, ranked — and nowhere else. It is
  // therefore the ONE place in the codebase where "a Solrend was just
  // won" is already true, which is why the war tally rides it instead
  // of minting a fourth win path that three of four future call sites
  // would forget.
  //
  // The extra read is deliberate: the alternative is threading the
  // faction through three call signatures for a value that is one
  // shallow read of a string, and a signature change is how the
  // fourth caller ends up passing undefined.
  //
  // Fire-and-forget, and fenced in its own try: an unfactioned player,
  // an offline write, a permission change — none of them may ever turn
  // a won duel into a thrown error.
  try {
    const fSnap = await get(ref(db, `profiles/${uid}/faction`));
    let faction = fSnap.val();
    // THE JESTERS ARE A BOOLEAN, NOT A VALUE. Joining J'ynxi writes
    // `isJester: true` and never touches `faction`, so reading one key
    // scored every Jester win as nobody's -- tally/jesters sat at 0 for
    // the life of the feature and `title_war_jesters` was unreachable.
    // The second read only happens for a player the first read missed.
    if (!faction) {
      const jSnap = await get(ref(db, `profiles/${uid}/isJester`));
      if (jSnap.val() === true) faction = "jesters";
    }
    if (faction) await tallyFactionWin(uid, faction);
  } catch (e) {
    console.warn("[factionWars] tally skipped (non-fatal):", e?.message);
  }
};

// ── ◕ CHOCOLATE COINS ─────────────────────────────────────────
// Terror's own money. Terror is the ONLY writer of `chocolateCoins`.

// HARDENED: the balance is mutated via an atomic transaction on the
// balance node itself — a delta, never a full-object overwrite. Two tabs /
// a network blip can no longer read-then-clobber the balance. The audit
// log is appended separately (push-style key, never overwrites siblings).
// (This hardening predates the 2026-07-28 rename and carries over intact —
// the write pattern is the whole point, not the field name.)
export const awardCoins = async (uid, amount, reason = "") => {
  uid = _uidKey(uid, "awardCoins");
  if (!uid) return;
  const balRef = ref(db, `profiles/${uid}/chocolateCoins`);
  const result = await runTransaction(balRef, (current) =>
    Math.max(0, (current || 0) + amount)
  );
  if (!result.committed) return;
  // Append-only audit entry — separate write, cannot disturb the balance.
  await update(ref(db, `profiles/${uid}`), {
    [`coinHistory/${Date.now()}`]: { amount, reason },
  });
  return result.snapshot.val();
};

// `awardCrests` removed 2026-08-18 — it forwarded to the COIN lane
// under a DC name. To put ◆ into a profile use grantCrests(); to put ◕
// in use awardCoins(). Its only importer was UnstablePierre.js, which
// never called it.

// ── SWEET TREAT ───────────────────────────────────────────────
// Plaza-Willy's first-visit grant. Atomic write of crests + flag
// in a single update() call so a network blip mid-grant cannot
// double-pay. Idempotent — refuses if already claimed.
//
// Returns { newTotal, alreadyClaimed }:
//   - { newTotal: <number>, alreadyClaimed: false }  on grant
//   - { newTotal: null,     alreadyClaimed: true  }  if already received
//   - null                                            if no profile (shouldn't happen)
export const awardSweetTreat = async (uid) => {
  uid = _uidKey(uid, "awardSweetTreat");
  if (!uid) return null;
  const SWEET_TREAT_AMOUNT = 5;
  const stamp = Date.now();

  // HARDENED: the read (idempotency check) and the write (crests + flag +
  // audit) now happen inside ONE transaction on the profile node, so the
  // "already claimed?" check and the payout can never be split by a race.
  const result = await runTransaction(
    ref(db, `profiles/${uid}`),
    (profile) => {
      if (!profile) return profile;                 // no profile — abort, leave untouched
      if (profile.hasReceivedSweetTreat) return;    // already claimed — abort transaction
      profile.chocolateCoins = (profile.chocolateCoins || 0) + SWEET_TREAT_AMOUNT;
      profile.hasReceivedSweetTreat = true;
      profile.coinHistory = profile.coinHistory || {};
      profile.coinHistory[stamp] = {
        amount: SWEET_TREAT_AMOUNT,
        reason: "first_visit_sweet_treat",
      };
      return profile;
    }
  );

  if (!result.committed || !result.snapshot.exists()) {
    return { newTotal: null, alreadyClaimed: true };
  }
  return { newTotal: result.snapshot.val().chocolateCoins, alreadyClaimed: false };
};

export const saveLoadout = async (uid, loadout) => {
  // loadout: { grimoire, luxmoire, prophecy, curios, esclings }
  uid = _uidKey(uid, "saveLoadout");
  if (!uid) return false;
  const profileRef = ref(db, `profiles/${uid}`);
  await update(profileRef, { loadout });
  return true;
};

// ══════════════════════════════════════════════════════════════
// THE SETS — three named presets, exactly one equipped
// ══════════════════════════════════════════════════════════════
// caP'n, 2026-08-17: "let the user save up to 3 different combos
// and be able to name the preset and easily click and equip the
// set (a set can contain all of the following for a true proper
// preset experience: Companion, 3 Esclings, 2 Curios, Prophecy,
// Grimoire, Luxmoire)."
//
// ── THE MIRROR LAW (the whole reason this is cheap) ───────────
// `loadout` already has readers in App.js (twice), Dungeon1,
// WorldMap and Plaza, and every one of them is asking the same
// question: "what deck am I playing with." So the sets do NOT
// replace it. `loadout` becomes a MIRROR of the equipped set, and
// `saveSet` / `equipSet` below are its ONLY writers from here on.
// Nothing downstream changes, and a save written before today
// reads as Set 1 with no migration step and no lost deck.
//
// The mirror is a deliberate second copy of the same data, which
// normally is exactly the drift this codebase legislates against.
// It survives that test on one condition, stated here so a future
// reader can check it: TWO functions write it, they are in this
// file, and they write it in the same call as the set itself. A
// third writer of `loadout` anywhere is a bug even if the value
// it writes is right.
//
// ── WHAT A SET IS ────────────────────────────────────────────
//   name      — the player's word for it. Never blank on read.
//   grimoire  — {cardId: qty}         the deck
//   luxmoire  — {cardId: qty}         the side deck
//   variants  — {cardId: [{tier,source}]}  which COPY of each card
//   curios    — [id|null, id|null]    2 rifts, pre-placed
//   prophecy  — id|null               1 rift, pre-placed
//   esclings  — [id|null, id|null, id|null]  the three that OPEN
//   companion — id|null               who stands beside you
//
// `esclings` is new to the whole game. Before today all three
// slots were auto-filled from the first Escendant found in the
// Grimoire (Shatterfield's buildInitialEsclings), so every deck
// opened on three identical Esclings and the player never had a
// say. That fallback is untouched and still fires for any set
// that leaves the triple null — an unchosen board is not broken,
// it is exactly as good as it was yesterday.
//
// FIREBASE: profiles/{uid}/sets/{0|1|2} + profiles/{uid}/setActive
// Both are NEW TOP-LEVEL PROFILE FIELDS. Per IE_FIREBASE_LAW §1
// the hard `$extra` lock lives only on `collection/$cardId`; every
// other player-level field is permitted-but-unvalidated by design,
// so this needs NO rules deploy. (The Whitelist Trap applies to
// collection children and nothing else — see TERROR_PRIME §2.2.)
// ══════════════════════════════════════════════════════════════

// ══════════════════════════════════════════════════════════════
// THE STORY SET — what walking the whole world is worth
// ══════════════════════════════════════════════════════════════
// caP'n, 2026-08-17: "doing story mode with absolutely no easter
// eggs will yield you one of every card... completing story mode
// yields a fable version of 1 of each card but of course as is law,
// its a new source so it requires a new sigil and that will be what
// warrants the chase here. then doing every single easter egg will
// yield the super cool version of the sigil... and of course
// mythical holos of every card instead of the basic, no legendary
// version, just fable(no holo) and mythical(true unique animation
// holo)."
//
// TWO TIERS, AND ONLY TWO. There is deliberately no legendary rung
// between them — caP'n said so outright, and the reason it is worth
// obeying rather than smoothing is that a middle tier would make
// the whole-world run the SECOND-best outcome instead of the only
// other one. Chases work when there are two answers.
//
// ── WHY THE STAMP COMES BEFORE THE GRANT (a knowing exception) ──
// GRANT-BEFORE-FLAG is the standing law and it is right for
// purchases: a failed equip must never burn a coin. This is the
// case it is wrong for. The grant here is ~200 separate
// transactions, and the two failure modes are not symmetric — a
// partial grant leaves a player short some cards and is repaired by
// running it again with the stamp cleared, while a double grant
// puts a second copy of EVERY CARD IN THE GAME into a collection
// and cannot be walked back at all without the reset that NEVER
// SILENTLY RESET forbids. So: stamp first, abort if stamped, then
// pour. The same order worldMap's recordNodeClear already uses.
// ══════════════════════════════════════════════════════════════
// THE MINERIUM POUR — one node, one bundle, two states
// ══════════════════════════════════════════════════════════════
// ⭐ THE BLUEPRINT LAW (caP'n, 2026-08-22): a node pays the SAME
// bundle either way — cleared the ordinary way it arrives in Fable,
// cleared the EGG way it arrives in Mythic wearing `holo_vrown`.
// `minerium1Rewards.NODE_LOOT` is the whole map; this file only knows
// how to pay a node and how never to pay it twice.
//
// ── WHAT THIS REPLACED ─────────────────────────────────────────
// Twelve hours ago there were wedges, regions, chambers, two
// different maps, two floors and a top-up. All of it is gone. The
// reason is caP'n's, and it is the right one: under the old shape a
// Sol King beaten perfectly could pay nothing, because its Mythic
// wedge lived somewhere else on a different track's map. One table
// with two states cannot have that bug — there is nowhere for it to
// hide.
//
// ── THE STAMP ──────────────────────────────────────────────────
//   profiles/{uid}/storyProgress/minerium1/{track}/{nodeId}
// A field under an existing profile node, so no rules deploy and never
// a new top-level node (IE Firebase law). One stamp per node per
// track, so a re-walked node is free and a half-landed pour is
// repaired by clearing ONE node's stamp instead of a whole set.
//
// ── WHY THE STAMP COMES BEFORE THE GRANT (a knowing exception) ──
// GRANT-BEFORE-FLAG is the standing law and it is right for purchases:
// a failed equip must never burn a coin. This is the case it is wrong
// for. A partial pour leaves a player short three cards and is
// repaired by clearing one stamp; a double pour puts a second copy
// into a collection and cannot be walked back at all without the reset
// that NEVER SILENTLY RESET forbids.
const m1Ref = (uid, track, nodeId) =>
  ref(db, `profiles/${uid}/storyProgress/minerium1/${track}/${String(nodeId).replace(/[.#$/[\]]/g, "_")}`);

// The one key under a track that is not a node: proof the legacy seal
// has run. `__` cannot collide with a node id.
const SEAL_MARK = "__sealed";

// One of every card that can live in a collection. Kept because the
// audit and a couple of tools still ask for it; the pour itself works
// off NODE_LOOT now. Esclings are excluded on purpose — they are field
// furniture, and nothing has ever put one in a collection.
const storySetCardIds = () => {
  const ids = [];
  try {
    for (const c of getAllGrimoireCards()) if (c?.id) ids.push(c.id);
    for (const c of getAllImpera())      if (c?.id) ids.push(c.id);
  } catch (_) { /* an empty list is survivable; a throw here is not */ }
  return Array.from(new Set(ids));
};

// ⚠ THE LEGACY SEAL. Two kinds of profile are already holding cards
// this system would hand out again:
//   1. one that tripped the old all-or-nothing `grantStorySet` and is
//      holding the entire 177-card set under story_fable/story_mythic;
//   2. one that ran the WEDGE build from earlier the same day, whose
//      stamps are keyed "region:dais" / "secret:ACL-26" / "throne" —
//      shapes no node id can ever take.
// Either way the fix is the same: stamp every node on that track as
// already-paid and pour nothing. Without this, the upgrade itself is
// the double pour. Costs one read.
async function sealLegacyTrack(uid, track, stamps, nodeIds) {
  if (!uid) return true;                          // no uid, no pour — refuse, never guess
  if (stamps && stamps[SEAL_MARK]) return true;   // already sealed
  const wedgeShaped = stamps && Object.keys(stamps).some(k => k.includes(":") || k === "throne");
  try {
    let legacy = wedgeShaped;
    if (!legacy) {
      const old = await get(ref(db, `profiles/${uid}/storyProgress/storySet/${track}`));
      legacy = old.exists();
    }
    if (!legacy) return false;
    for (const id of nodeIds) {
      await runTransaction(m1Ref(uid, track, id), (cur) => (cur ? undefined : { legacy: true, at: Date.now() }));
    }
    await runTransaction(m1Ref(uid, track, SEAL_MARK), (cur) => (cur ? undefined : Date.now()));
    if (stamps) stamps[SEAL_MARK] = true;
    return true;
  } catch (e) {
    // A read failure must not become a double pour. Refusing is the
    // safe direction; the node is still there next clear.
    console.warn("[minerium1] legacy seal check failed, holding the pour:", e && e.message);
    return true;
  }
}

// ONE READ PER TRACK, not one transaction per node. The transactions
// below are still authoritative — they are what makes two tabs racing
// safe — but this read means a re-walked world costs a read instead of
// fifty-five aborted writes.
const readTrackStamps = async (uid, track) => {
  try {
    const snap = await get(ref(db, `profiles/${uid}/storyProgress/minerium1/${track}`));
    return snap.exists() ? (snap.val() || {}) : {};
  } catch (_) { return null; }   // null = could not read; fall through to the transactions
};

// ── THE PUPIL'S PRIZE ──────────────────────────────────────────
// ⭐ caP'n: *"the middle pupil will give you a Mythical SHV Backdrop
// and Coin and Lux/Grim aura."* The one node in the Minerium that pays
// no card, because it is the deepest door in the world and a card is
// not enough. Cost 0 on both unlocks — this is earned, not bought, and
// the shop's own unlock functions are reused rather than a fourth
// grant path being invented (they are already append-only and
// idempotent).
async function pourPupilCosmetics(uid, source) {
  const out = [];
  const P = MINERIUM1_PUPIL;
  // ⚠ THESE THREE RETURN OBJECTS, NOT BOOLEANS. `unlockBackdrop` and
  // `unlockCoinSkin` return { ok, reason } and `grantCosmetic` returns
  // { success } — INCLUDING on failure, so `if (await unlock…)` is true
  // every single time. The first draft did exactly that: the Pupil
  // reported three cosmetics granted whether or not any landed, and
  // since the node's run-stamp is already committed by then, the ribbon
  // said "The Eye opens" over an empty grant with no way to retry.
  // Caught by an adversarial pass. Read the field, not the truthiness.
  try { if ((await unlockBackdrop(uid, P.backdrop, 0, source))?.ok) out.push(P.backdrop); } catch (_) {}
  try { if ((await unlockCoinSkin(uid, P.coinSkin, 0, source, "DC"))?.ok) out.push(P.coinSkin); } catch (_) {}
  try { if ((await grantCosmetic(uid, P.aura, { source, rarity: "mythic", slot: "aura" }))?.success) out.push(P.aura); } catch (_) {}
  return out;
}

// Pay ONE node on ONE track. Returns how many cards actually landed
// (0 if the node was already stamped). Never throws — a reward screen
// that crashes is worse than one that says nothing.
export const pourMinerium1Node = async (uid, nodeId, track = "fable") => {
  uid = _uidKey(uid, "pourMinerium1Node");
  if (!uid || !nodeId) return 0;
  track = track === "mythic" ? "mythic" : "fable";
  const loot = lootForNode(nodeId);
  if (!loot) return 0;

  const claim = await runTransaction(m1Ref(uid, track, nodeId), (cur) => (cur ? undefined : Date.now()));
  if (!claim.committed) return 0;

  const source = MINERIUM1_SOURCE[track];
  const isHolo = track === "mythic";

  // The Pupil pays cosmetics on BOTH tracks and cards on neither. A
  // backdrop has no foil, so there is deliberately no "Mythic variant"
  // here — stated rather than left implicit, so the next Minerium does
  // not invent one to fill a symmetry that was never real.
  if (loot.cosmeticsOnly) {
    const got = await pourPupilCosmetics(uid, `minerium1:${nodeId}:${track}`);
    return got.length;
  }

  let poured = 0, failed = 0;
  for (const id of loot.cards) {
    try {
      await addCardToCollection(uid, id, isHolo, isHolo ? MINERIUM1_MYTHIC_HOLO_TIER : null, source);
      poured++;
    } catch (_) { failed++; }
  }
  // Reported, not swallowed. The repair is to clear this one node's
  // stamp, and the id is in the line.
  if (failed) console.warn(`[minerium1] ${track}/${nodeId}: ${failed} of ${loot.cards.length} cards failed to land`);
  return poured;
};

// THE POUR, called once per clear from worldData.recordNodeClear.
// `firstClear` pays Fable, `firstSecret` pays Mythic, and a node egged
// on the very first attempt pays BOTH in the same call — because
// solving the puzzle counts as clearing the node, and caP'n's ruling
// on that is explicit: *"the player doesn't have to do it twice for no
// reason, they solved the impossible, they should be rewarded not
// punished."*
export const pourMinerium1 = async (uid, nodeId, { firstClear = false, firstSecret = false } = {}) => {
  uid = _uidKey(uid, "pourMinerium1");
  if (!uid || !nodeId || (!firstClear && !firstSecret)) return null;
  const loot = lootForNode(nodeId);
  if (!loot) return null;

  const out = { node: nodeId, label: loot.label, fable: 0, mythic: 0, cosmetics: !!loot.cosmeticsOnly };
  const nodeIds = Object.keys(NODE_LOOT);

  const run = async (track) => {
    const stamps = await readTrackStamps(uid, track);
    if (await sealLegacyTrack(uid, track, stamps, nodeIds)) return 0;   // ⚠ see sealLegacyTrack
    if (stamps && stamps[nodeId]) return 0;                             // already paid — do not even ask
    return pourMinerium1Node(uid, nodeId, track);
  };

  try {
    if (firstClear)  out.fable  = await run("fable");
    if (firstSecret) out.mythic = await run("mythic");
  } catch (e) {
    // Never let a reward pour break the clear that earned it. The
    // stamps are already down; whatever did not land is due next time.
    console.warn("[minerium1] pour failed mid-run:", e && e.message);
  }
  return (out.fable || out.mythic) ? out : null;
};

// ── grantStorySet — RETIRED, kept as a shim ────────────────────
// Its call sites are long gone. It survives because deleting a public
// export that another title's vendored copy may still reference is a
// break for no gain, and because a save tool may legitimately want the
// old all-at-once behaviour. It pours through the node map now, so it
// can never disagree with the blueprint.
export const STORY_SET_SOURCE = { fable: "story_fable", mythic: "story_mythic" }; // ⚠ LEGACY — read, never written
export const grantStorySet = async (uid, tier = "fable") => {
  // ⚠ THE CHOKEPOINT. Every profile writer goes through _uidKey; a
  // caller passing a display name would otherwise create
  // profiles/<Name>/... — the shadow node this file's opening banner
  // exists to make structurally impossible.
  uid = _uidKey(uid, "grantStorySet");
  const track = tier === "mythic" ? "mythic" : "fable";
  if (!uid) return { granted: false, why: "bad uid", tier: track };
  const nodeIds = Object.keys(NODE_LOOT);
  const stamps = await readTrackStamps(uid, track);
  if (await sealLegacyTrack(uid, track, stamps, nodeIds)) {
    return { granted: false, already: true, tier: track };
  }
  let poured = 0;
  for (const id of nodeIds) poured += await pourMinerium1Node(uid, id, track);
  return { granted: poured > 0, tier: track, source: MINERIUM1_SOURCE[track], poured, total: storySetCardIds().length };
};

// ══════════════════════════════════════════════════════════════
// THE STARTER SHELF — every starter deck, buyable
// ══════════════════════════════════════════════════════════════
// caP'n, 2026-08-17: "we are also going to add all starter decks to
// the booster shop and make a starter deck section so when I make
// more."
//
// "SO WHEN I MAKE MORE" IS THE SPEC. The shelf is not a list of
// twelve decks — it is STARTER_DECKS, rendered. A thirteenth deck
// appears in the shop the moment it is authored in starterDecks.js,
// with no edit here and none in BoosterPack.js. If adding content
// ever needs a code change, the design is wrong.
//
// A bought starter carries the SAME `starter` sigil as the one you
// were handed at onboarding, and that is deliberate. The mark says
// "from a Starter Deck", which is true of both; inventing a
// `starter_shop` source would split one provenance in two and make
// the Codex tell a player their own cards came from two different
// places because of which door they walked through.
//
// Re-buying a deck you already own is ALLOWED. It is the cheapest
// route to a second copy of a common, it is a coin sink, and the
// alternative — a one-per-account gate — turns the shelf into a
// checklist you clear once and never open again.
export const STARTER_DECK_COST = 1;   // ⚠ TEST PRICING 2026-08-19 -- was 25   // ◕ — a DIAL. ~45 cards against a
                                       // 10-card pack at 5, priced to sit
                                       // beside a boutique pack rather than
                                       // under one. Move it in playtest.

// Everything a starter deck actually hands over: both lanes, plus
// the pre-placed Curios and Prophecy — those are cards you own, and
// leaving them out was the shape of the old bug where a player had
// a Curio rift and nothing legal to put in it.
export const starterDeckContents = (deck) => {
  if (!deck) return [];
  const out = [];
  for (const [id, qty] of Object.entries(deck.grimoire || {})) for (let i = 0; i < qty; i++) out.push(id);
  for (const [id, qty] of Object.entries(deck.luxmoire || {})) for (let i = 0; i < qty; i++) out.push(id);
  for (const id of (deck.curios || [])) if (id) out.push(id);
  if (deck.prophecy) out.push(deck.prophecy);
  return out;
};

// SPEND FIRST, THEN POUR — spendCoins is an atomic check-and-debit
// transaction that aborts on an insufficient balance, so a refused
// purchase costs nothing and a successful one cannot be double-spent
// by two taps. Only after the coins are actually gone do the cards
// land. Reverse it and a player with 24 coins walks away with a deck.
export const buyStarterDeck = async (uid, deckId) => {
  uid = _uidKey(uid, "buyStarterDeck");
  if (!uid) return { ok: false, why: "Not signed in." };
  const deck = STARTER_DECKS.find(d => d.id === deckId);
  if (!deck) return { ok: false, why: "No such deck." };

  const paid = await spendCoins(uid, STARTER_DECK_COST);
  if (!paid) return { ok: false, why: "Not enough Chocolate Coins." };

  const ids = starterDeckContents(deck);
  let poured = 0, failed = 0;
  for (const id of ids) {
    try { await addCardToCollection(uid, id, false, null, "starter"); poured++; }
    catch (_) { failed++; }
  }
  if (failed) console.warn(`[starterShelf] ${deckId}: ${failed} of ${ids.length} cards failed to land`);
  return { ok: true, deck, poured, failed, total: ids.length, spent: STARTER_DECK_COST };
};

export const SET_SLOTS = 3;

const _emptySet = (i) => ({
  name: `Set ${i + 1}`,
  grimoire: {},
  luxmoire: {},
  variants: {},
  curios: [null, null],
  prophecy: null,
  // ESCLING_SLOTS wide, not a literal 3: this is the shape a brand-new set
  // is written in, and a three-wide empty set would mean the fourth rift has
  // no key to save into — the player's fourth Escling would be dropped on
  // every save, silently.
  esclings: Array(ESCLING_SLOTS).fill(null),
  companion: null,
  savedAt: 0,
});

// Defensive on every axis, because a set is player-authored data
// that has to survive a schema that will grow. Anything missing
// becomes its empty form; anything the wrong SHAPE becomes its
// empty form. A set never throws and never returns undefined.
const _normalizeSet = (raw, i) => {
  const base = _emptySet(i);
  if (!raw || typeof raw !== "object") return base;
  const pad = (arr, n) => {
    const out = Array.isArray(arr) ? arr.slice(0, n) : [];
    while (out.length < n) out.push(null);
    return out.map(v => (typeof v === "string" && v ? v : null));
  };
  return {
    name: (typeof raw.name === "string" && raw.name.trim()) ? raw.name.trim().slice(0, 24) : base.name,
    grimoire: (raw.grimoire && typeof raw.grimoire === "object") ? raw.grimoire : {},
    luxmoire: (raw.luxmoire && typeof raw.luxmoire === "object") ? raw.luxmoire : {},
    // `variants` is this file's name for what DeckBuilder calls
    // deckVariants. Both spellings are read so a set saved by
    // either generation of the Forge loads; only `variants` is
    // ever written.
    variants: (raw.variants && typeof raw.variants === "object")
      ? raw.variants
      : ((raw.deckVariants && typeof raw.deckVariants === "object") ? raw.deckVariants : {}),
    curios: pad(raw.curios, 2),
    prophecy: (typeof raw.prophecy === "string" && raw.prophecy) ? raw.prophecy : null,
    // ⚠ SCHEMA CHANGE, 2026-09-07 (three rifts → four). Every set the player
    // saved before this wave was written three-wide and comes back three-wide.
    // pad() is deliberately the read path for it: it KEEPS the stored entries
    // and appends nulls up to ESCLING_SLOTS, so an existing set retains the
    // three Esclings its owner chose and simply gains an empty fourth rift.
    // Replacing rather than padding here would discard real player choices.
    esclings: pad(raw.esclings, ESCLING_SLOTS),
    companion: (typeof raw.companion === "string" && raw.companion) ? raw.companion : null,
    savedAt: Number(raw.savedAt) || 0,
  };
};

// PURE. Takes a profile snapshot you already have and returns the
// three sets plus which one is equipped — no network call, so the
// Forge can render its rail on the same frame it mounts.
//
// THE LEGACY FOLD: a profile with no `sets` node but with a
// `loadout` is every account that existed before today. Its
// loadout becomes Set 1, in place, without writing anything. The
// player sees their deck where they expect it and finds out sets
// exist by noticing there are now two empty ones beside it.
export const readSets = (profile) => {
  const active = Math.min(Math.max(Number(profile?.setActive) || 0, 0), SET_SLOTS - 1);
  const stored = profile?.sets;
  const sets = [];
  for (let i = 0; i < SET_SLOTS; i++) {
    const raw = stored ? stored[i] : (i === 0 ? profile?.loadout : null);
    sets.push(_normalizeSet(raw, i));
  }
  return { sets, active };
};

// The equipped set, flattened into the exact shape every existing
// deck reader already expects. This is what gets mirrored onto
// `loadout`, and it is the only place that translation is written.
const _setToLoadout = (set) => ({
  grimoire: set.grimoire || {},
  luxmoire: set.luxmoire || {},
  prophecy: set.prophecy || null,
  curios: set.curios || [null, null],
  // The loadout mirror. Sets reaching here have already been through
  // _normalizeSet, so `set.esclings` is ESCLING_SLOTS-wide; the fallback has
  // to match that width or a set with no esclings node would mirror a
  // three-wide array onto `loadout` and hand the duel a short battle line.
  esclings: set.esclings || Array(ESCLING_SLOTS).fill(null),
  deckVariants: set.variants || {},
});

// Write one set. If it is the equipped one, the mirror moves with
// it in the SAME update() — one write, so the deck and the copy of
// the deck can never be one call out of step with each other.
export const saveSet = async (uid, slot, payload) => {
  uid = _uidKey(uid, "saveSet");
  if (!uid) return false;
  const i = Math.min(Math.max(Number(slot) || 0, 0), SET_SLOTS - 1);
  const set = _normalizeSet({ ...payload, savedAt: Date.now() }, i);
  const patch = { [`sets/${i}`]: set };

  // Read the active index rather than trusting the caller: the
  // Forge can be open in one tab while a set is equipped in
  // another, and the mirror must follow the DB's idea of active.
  let active = 0;
  try {
    const snap = await get(ref(db, `profiles/${uid}/setActive`));
    active = Math.min(Math.max(Number(snap.val()) || 0, 0), SET_SLOTS - 1);
  } catch (_) { /* absent means slot 0, which is the default anyway */ }

  if (active === i) patch.loadout = _setToLoadout(set);
  await update(ref(db, `profiles/${uid}`), patch);
  if (active === i && set.companion) {
    // Companion lives in `equipped` because it is a COSMETIC and
    // cosmetics equip anywhere and appear everywhere (Cosmetic
    // Rule). The set does not own it — it remembers it, and
    // equipping the set re-asserts it. Never cleared from here:
    // a set with no companion chosen leaves whoever is already
    // walking beside you alone.
    await update(ref(db, `profiles/${uid}/equipped`), { companion: set.companion });
  }
  return true;
};

// Equip a set: move the pointer, refresh the mirror, and re-assert
// the companion. Reads the set back from the DB rather than taking
// it as an argument, so "equip" can be a one-tap button anywhere in
// the game without that caller needing to hold the deck.
export const equipSet = async (uid, slot) => {
  uid = _uidKey(uid, "equipSet");
  if (!uid) return false;
  const i = Math.min(Math.max(Number(slot) || 0, 0), SET_SLOTS - 1);
  let raw = null;
  try {
    const snap = await get(ref(db, `profiles/${uid}/sets/${i}`));
    raw = snap.val();
  } catch (_) { raw = null; }

  // Equipping an empty slot is a legal, deliberate no-op on the
  // deck: we move the pointer so the Forge opens on that slot, but
  // we do NOT mirror an empty grimoire over a working one. Handing
  // a player an empty deck because they tapped an empty slot is
  // the kind of destruction NEVER SILENTLY RESET exists to stop.
  const set = _normalizeSet(raw, i);
  const isEmpty = !raw || Object.keys(set.grimoire || {}).length === 0;
  const patch = { setActive: i };
  if (!isEmpty) patch.loadout = _setToLoadout(set);
  await update(ref(db, `profiles/${uid}`), patch);
  if (!isEmpty && set.companion) {
    await update(ref(db, `profiles/${uid}/equipped`), { companion: set.companion });
  }
  return !isEmpty;
};

// HARDENED: check-balance-and-debit is a single atomic transaction on
// the chocolateCoins node. The old read-then-write could let two purchases
// both pass the "enough?" check and double-spend, or clobber a
// concurrent award. The transaction aborts (returns undefined) when the
// balance is insufficient, so spendCoins still returns false correctly.
export const spendCoins = async (uid, amount) => {
  uid = _uidKey(uid, "spendCoins");
  if (!uid || amount <= 0) return false;
  const balRef = ref(db, `profiles/${uid}/chocolateCoins`);
  // ⚠ THE EMPTY-CACHE REFUSAL (2026-09-16, same bug ieEconomy.spendCrests
  // had): a transaction's FIRST run can see null because the node isn't
  // loaded yet. Treating that as 0 refused players who could pay. Hand the
  // null back so the handler re-runs on the server's value; `charged` is
  // decided per run so a no-op commit never reads as paid.
  let charged = false;
  const result = await runTransaction(balRef, (current) => {
    charged = false;
    if (current === null || current === undefined) return current;
    if (current < amount) return;      // abort — not enough, leave balance untouched
    charged = true;
    return current - amount;
  });
  return !!(result && result.committed && charged);
};

// `spendCrests` removed 2026-08-18. THE LANDMINE: ieEconomy.js exports a
// function of exactly this name that debits real ◆, and this one debited
// ◕. Two functions, one name, opposite currencies, one import line
// between them. Use spendCoins() for ◕ and spendDivineCrests() for ◆.

// ── COLLECTION ────────────────────────────────────────────────

// HARDENED — this is the function behind the holo-wipe.
// The old version did get() → set(). If the get() ever returned empty for a
// card that actually existed (offline cache miss, second tab, network blip),
// the set() wrote a fresh {count:1} object and DESTROYED holoCount/holoTier.
// Now it's a single atomic transaction on the card node: the increment is
// always applied to whatever the server currently holds, so existing holo
// data is mathematically preserved. The transaction merges onto `current`
// rather than building a fresh object, so any future fields survive too.
// `source` is the PROVENANCE id (see SourceSigil.SOURCE_SIGILS): where this
// specific card came from — "earned", "vault", "booster_shadowfox", etc.
// It is APPEND-ONLY, two ways:
//   sources      : {sourceId: count}            — how many of this card from
//                                                  each source, foil or not.
//   sourceTiers  : {tier: {sourceId: count}}    — the per-FOIL provenance the
//                                                  Forge reads: "Solrend ×1
//                                                  from Shadow Fox, Solrend ×1
//                                                  from Vault." This is what
//                                                  puts the NUMBER on a foil
//                                                  swatch and fills its picker.
// A non-holo grant records under `sources` only (it has no tier). A holo grant
// records under both. Default "" source = legacy/unknown grant; we DON'T stamp
// an empty source into the maps (keeps old call sites clean), so a card with no
// recorded provenance simply has no sigil data and the viewer shows fallback.
export const addCardToCollection = async (uid, cardId, isHolo = false, holoTier = null, source = "") => {
  uid = _uidKey(uid, "addCardToCollection");
  if (!uid || !cardId) return;
  const cardRef = ref(db, `profiles/${uid}/collection/${cardId}`);
  await runTransaction(cardRef, (current) => {
    const c = current || { count: 0, holoCount: 0, holoTier: null };
    // holoTiers is the SET (as a {tier: count} map) of every foil this card
    // is owned in — the per-tier truth the Forge + Foil Vault read. Append-only:
    // we only ever increment, never clear, so an existing record is never wiped.
    // Legacy `holoTier` (last foil obtained) is kept in sync for back-compat.
    const tiers = { ...(c.holoTiers || {}) };
    if (isHolo && holoTier) tiers[holoTier] = (tiers[holoTier] || 0) + 1;

    // ── PROVENANCE (append-only, same atomic transaction) ────────────
    const sources = { ...(c.sources || {}) };
    const sourceTiers = {};
    for (const [t, m] of Object.entries(c.sourceTiers || {})) {
      sourceTiers[t] = { ...(m || {}) };
    }
    if (source) {
      sources[source] = (sources[source] || 0) + 1;
      if (isHolo && holoTier) {
        const m = sourceTiers[holoTier] || (sourceTiers[holoTier] = {});
        m[source] = (m[source] || 0) + 1;
      }
    }

    return {
      ...c,
      // non-holo grant adds a count; holo grant guarantees count ≥ 1
      count: Math.max((c.count || 0) + (isHolo ? 0 : 1), isHolo ? 1 : 0),
      holoCount: isHolo ? (c.holoCount || 0) + 1 : (c.holoCount || 0),
      holoTier: holoTier || c.holoTier || null,
      holoTiers: tiers,
      sources,
      sourceTiers,
    };
  });
  // Evaluate collection-shaped achievements off the fresh state. Idempotent;
  // non-fatal — a failure here must never break a card grant.
  try { await checkCollectionAchievements(uid); } catch (_) { /* non-fatal */ }
};

export const getCollection = async (uid) => {
  uid = _uidKey(uid, "getCollection");
  if (!uid) return {};
  const snap = await get(ref(db, `profiles/${uid}/collection`));
  return snap.exists() ? snap.val() : {};
};

// ═══════════════════════════════════════════════════════════════
// T21 · THE SCRAP LANE — dupes become Chocolate Coins, BY CHOICE
// ───────────────────────────────────────────────────────────────
// MANUAL, ALWAYS (caP'n's standing ruling, 2026-08-18): a dupe pays
// nothing until the player chooses to scrap it. No auto-pay at the
// pull, no retroactive sweep. Two surfaces call this engine — the
// Forge lightbox and the booster reveal — and both go through here.
//
// THE FLOOR OF ONE (V'ael's call, labeled, reversible): the LAST
// copy of a card can never be scrapped — this is a dupe lane; the
// collection is the soul. Concretely: plain scraps require count ≥ 2
// (count also encodes bare ownership, so it never drops below 1 once
// a card is owned). Foil scraps only ever touch holoTiers/holoCount,
// so ownership survives them by construction — scrapping your only
// foil of a card melts the FINISH; the card survives as a plain copy.
//
// ⚖ RATE DIALS — set at build, tuned in play, never asked about.
// Ladder is proportional to FOIL_VAULT_PRICE (storyData.js), shaded
// down so the worst boutique loop (buy an all-foil pack → scrap all
// ten) lands near +1–2× pack cost instead of a money printer:
//   all_stars_mythic 20◕ → 10 mythics × 4 = 40◕ · dragons_wrath_solrend
//   14◕ → 30◕ · solar_ascension_gold 12◕ → 20◕. Post-collection
//   endgame slack, deliberately smaller per minute than dueling
//   (NPC_WIN 75◕). Tuning session: these eight numbers + `plain`.
export const SCRAP_COIN = {
  plain: 1, // every plain dupe
  tiers: {
    holo_silver: 1, holo_gold: 2, holo_khao: 2, holo_jynxi: 2,
    holo_depths: 3, holo_solrend: 3, holo_aeternus: 3, holo_mythic: 4,
  },
  retiredFloor: 1, // a retired tier (holo_shadow) melts at the silver floor
};

// What ONE copy of a given variant pays. `tier` = null for plain, else
// the tier key AS STORED in the collection (legacy keys welcome — they
// price as their canon tier but decrement under their stored name).
export const scrapValueOf = (tier) => {
  if (!tier) return SCRAP_COIN.plain;
  return SCRAP_COIN.tiers[canonTier(tier)] ?? SCRAP_COIN.retiredFloor;
};

// The scrap write. items: [{ cardId, tier: null|"<storedTierKey>", copies }].
// Per-card ATOMIC transaction on collection/$cardId (the one hard-validated
// node — rules verified 2026-08-18: count/holoCount/holoTiers/$tier/sources
// all validate ≥ 0, no monotonic guard, so the decrement passes as-is), then
// ONE delta transaction on the chocolateCoins leaf for the total. Never a
// read-then-overwrite anywhere (April 2026 law). Clamps to what actually
// exists, so a stale UI can never over-scrap — you get paid for what was
// really removed, nothing else.
// Provenance stays coherent: each removed copy also decrements the flat
// `sources` map (richest source first — which physical copy a scrap came
// from is unknowable, so this is the labeled approximation) and, for foils,
// the matching `sourceTiers[tier]` entry, so the Forge's sigil picker never
// offers a copy that no longer exists. Tierless legacy foils (isHolo grants
// that carried no tier — old dungeon mascots) have no ladder entry and are
// deliberately unreachable here; they keep their record and pay nothing.
// NOTE decks/sets are refs, not owners — a scrap that drops a card below a
// saved deck's usage surfaces as the Forge's normal deck warnings, by design.
export const scrapCopies = async (uid, items) => {
  uid = _uidKey(uid, "scrapCopies");
  if (!uid || !Array.isArray(items) || items.length === 0) {
    return { success: false, reason: "nothing-to-scrap", paid: 0, results: [] };
  }
  // Group requests by card so each card is exactly one transaction.
  const byCard = new Map();
  for (const it of items) {
    if (!it || !it.cardId) continue;
    const list = byCard.get(it.cardId) || [];
    list.push({ tier: it.tier || null, copies: Math.max(1, Math.floor(it.copies || 1)) });
    byCard.set(it.cardId, list);
  }

  const results = [];
  let totalPay = 0;

  for (const [cardId, reqs] of byCard) {
    const cardRef = ref(db, `profiles/${uid}/collection/${cardId}`);
    // ── CACHE SEED (critique fix, 2026-08-18) ────────────────────
    // runTransaction hands the updater the LOCAL CACHE first — null
    // when this node was never listened to. The Forge listens to the
    // whole profile so it never hits this; the booster reveal doesn't,
    // and returning undefined on that first null ABORTS with no server
    // consult — the scrap button would silently do nothing on a card
    // you are holding. One get() primes a seed; when `current` is null
    // we compute from the seed and RETURN the result — the SDK ships
    // it against expected-base null, the server rejects the mismatch,
    // and the updater re-runs with the REAL node (the documented retry
    // dance). A node that is truly absent stays a clean abort.
    let seed = null;
    try { seed = (await get(cardRef)).val(); } catch (_) { /* offline → cache-only path */ }
    // The txn body may re-run on contention; `outcome` is reset every run,
    // so after commit it holds exactly what the WINNING run removed.
    let outcome = null;
    const res = await runTransaction(cardRef, (current) => {
      outcome = [];
      const base = current || seed;
      if (!base) return; // nothing owned anywhere → abort, node untouched
      const c = { ...base };
      const tiers = { ...(c.holoTiers || {}) };
      const sources = { ...(c.sources || {}) };
      const sourceTiers = {};
      for (const [t, m] of Object.entries(c.sourceTiers || {})) sourceTiers[t] = { ...(m || {}) };
      let holoCount = c.holoCount || 0;

      const decRichest = (map) => {
        let best = null;
        for (const k of Object.keys(map)) {
          if ((map[k] || 0) > 0 && (best === null || map[k] > map[best])) best = k;
        }
        if (best === null) return null;
        map[best] -= 1;
        if (map[best] <= 0) delete map[best];
        return best;
      };

      for (const r of reqs) {
        let done = 0;
        if (!r.tier) {
          // PLAIN — the floor of one, enforced at the write itself.
          for (let i = 0; i < r.copies; i++) {
            if ((c.count || 0) <= 1) break;
            c.count -= 1;
            decRichest(sources);
            done++;
          }
        } else {
          for (let i = 0; i < r.copies; i++) {
            if ((tiers[r.tier] || 0) <= 0) break;
            tiers[r.tier] -= 1;
            if (tiers[r.tier] <= 0) delete tiers[r.tier];
            holoCount = Math.max(0, holoCount - 1);
            const m = sourceTiers[r.tier];
            if (m) {
              const src = decRichest(m);
              if (Object.keys(m).length === 0) delete sourceTiers[r.tier];
              if (src && (sources[src] || 0) > 0) {
                sources[src] -= 1;
                if (sources[src] <= 0) delete sources[src];
              }
            }
            done++;
          }
        }
        if (done > 0) outcome.push({ cardId, tier: r.tier, scrapped: done, paid: done * scrapValueOf(r.tier) });
      }

      if (outcome.length === 0) return; // nothing scrappable → abort clean

      // Keep the legacy `holoTier` pointer honest if its tier just emptied.
      let legacy = c.holoTier || null;
      if (legacy && !(tiers[legacy] > 0)) {
        legacy = null;
        for (const t of Object.keys(tiers)) { if (tiers[t] > 0) { legacy = t; break; } }
      }

      return {
        ...c,
        count: c.count || 0, // rules: hasChildren(['count']) — always present
        holoCount,
        holoTier: legacy,
        holoTiers: tiers,
        sources,
        sourceTiers,
      };
    });

    if (res.committed && Array.isArray(outcome) && outcome.length) {
      for (const o of outcome) { totalPay += o.paid; results.push(o); }
    }
  }

  if (totalPay <= 0) {
    return { success: false, reason: "nothing-to-scrap", paid: 0, results: [] };
  }

  // The coin delta — one transaction on the leaf, never the profile object.
  const balRes = await runTransaction(
    ref(db, `profiles/${uid}/chocolateCoins`),
    (cur) => (cur || 0) + totalPay
  );
  const newBalance = balRes.committed ? balRes.snapshot.val() : null;

  // Audit trail — same shape family as packHistory / foilVaultHistory, so
  // the future hardening pass can reconcile every coin this lane minted.
  try {
    await update(ref(db, `profiles/${uid}`), {
      [`scrapHistory/${Date.now()}`]: { paid: totalPay, items: results },
    });
  } catch (_) { /* non-fatal — the scrap itself already landed */ }

  return { success: true, paid: totalPay, results, newBalance };
};

// ═══════════════════════════════════════════════════════════════
// PROVENANCE BACKFILL — one-time, idempotent
// ───────────────────────────────────────────────────────────────
// Cards granted BEFORE the source-sigil system shipped have no `sources`
// data, so they show no origin sigil. This stamps the player's STARTER deck
// cards (which every account owns from day one) with the `starter` source —
// but ONLY on cards that have NO source recorded yet, so it never overwrites
// real provenance (a vault/pack/earned copy is left untouched) and is safe to
// run on every Forge load. It writes append-only via update() at the exact
// child paths, never a full-object overwrite, so nothing can be wiped.
//
// `starterCardIds` is the list of card ids in the player's starter deck —
// pass getStarterDeck(starterDeckId)'s grimoire keys + curios. No-op if the
// player has no starterDeckId or the list is empty.
export const backfillStarterProvenance = async (uid, starterCardIds = []) => {
  uid = _uidKey(uid, "backfillStarterProvenance");
  if (!uid || !Array.isArray(starterCardIds) || starterCardIds.length === 0) {
    return { success: true, stamped: 0, noChange: true };
  }
  const col = await getCollection(uid);
  const updates = {};
  let stamped = 0;
  for (const cardId of starterCardIds) {
    const rec = col[cardId];
    if (!rec) continue;                       // don't own it → nothing to stamp
    const hasSource = rec.sources && Object.keys(rec.sources).length > 0;
    const hasTierSource = rec.sourceTiers && Object.keys(rec.sourceTiers).length > 0;
    if (hasSource || hasTierSource) continue; // already has provenance → leave it
    // Append-only, exact-path write — never a full-object overwrite.
    updates[`profiles/${uid}/collection/${cardId}/sources/starter`] = 1;
    stamped++;
  }
  if (stamped === 0) return { success: true, stamped: 0, noChange: true };
  await update(ref(db), updates);
  return { success: true, stamped };
};

// ═══════════════════════════════════════════════════════════════
// COLLECTION ACHIEVEMENT EVALUATOR
// ───────────────────────────────────────────────────────────────
// One evaluator, run whenever the collection changes (every card
// enters through addCardToCollection, so that's the single hook).
// It derives every collection-shaped achievement from the live
// collection + profile and fires whatever thresholds are newly met.
// grantAchievement is idempotent, so re-running on every card add is
// safe and self-healing — a player who somehow skipped a grant gets
// it the next time any card lands. No scattered per-call-site grants.
//
// Covers: collector_50 / collector_100 / collector_all, holo_5,
// jynxi_holo, five_affinity, void_collector. (all_starters needs a
// per-deck win tracker that doesn't exist yet — wired separately.)
// ═══════════════════════════════════════════════════════════════

// Built once, lazily: cardId → affinity label, and the void-pack id set.
let _affinityIndex = null;
let _voidPackIds = null;
function _buildCardIndexes() {
  if (_affinityIndex && _voidPackIds) return;
  _affinityIndex = {};
  try {
    for (const c of getAllGrimoireCards()) {
      if (c && c.id && c.affinityType) _affinityIndex[c.id] = c.affinityType;
    }
  } catch (_) { _affinityIndex = {}; }
  _voidPackIds = new Set();
  try {
    const pool = PACK_POOLS.void_eclipse || {};
    for (const tierArr of Object.values(pool)) {
      if (Array.isArray(tierArr)) tierArr.forEach((id) => _voidPackIds.add(id));
    }
  } catch (_) { _voidPackIds = new Set(); }
}

// Total unique cards in the game (denominator for collector_all).
let _totalUnique = null;
function _getTotalUnique() {
  if (_totalUnique != null) return _totalUnique;
  try { _totalUnique = getAllGrimoireCards().filter((c) => c && c.id).length; }
  catch (_) { _totalUnique = 181; } // fallback to the known catalog size
  return _totalUnique;
}

export const checkCollectionAchievements = async (uid, collection = null) => {
  uid = _uidKey(uid, "checkCollectionAchievements");
  if (!uid) return;
  _buildCardIndexes();

  // Pull the collection if not supplied (post-write callers pass null).
  let coll = collection;
  if (!coll) {
    const snap = await get(ref(db, `profiles/${uid}/collection`));
    coll = snap.exists() ? snap.val() : {};
  }
  const ids = Object.keys(coll || {});
  if (ids.length === 0) return;

  // Derived metrics
  const uniqueCount = ids.length;
  let holoCount = 0;
  let hasJynxiHolo = false;
  const affinities = new Set();
  let hasVoid = false;

  for (const id of ids) {
    const entry = coll[id] || {};
    const hc = entry.holoCount || 0;
    holoCount += hc;
    // Per-tier truth: holoTiers map first, legacy single holoTier as fallback.
    const tierMap = entry.holoTiers || null;
    const ownsJynxi = tierMap
      ? (tierMap.holo_jynxi || 0) > 0
      : (hc > 0 && entry.holoTier === "holo_jynxi");
    if (ownsJynxi) hasJynxiHolo = true;
    const aff = _affinityIndex[id];
    if (aff) affinities.add(aff);
    if (_voidPackIds.has(id)) hasVoid = true;
  }

  const total = _getTotalUnique();

  // Fire whatever is newly met. grantAchievement no-ops if already owned.
  const grants = [];
  if (uniqueCount >= 50)    grants.push(["terror:collector_50",  "collection:unique50"]);
  if (uniqueCount >= 100)   grants.push(["terror:collector_100", "collection:unique100"]);
  if (uniqueCount >= total) grants.push(["terror:collector_all", "collection:uniqueAll"]);
  if (holoCount >= 5)       grants.push(["terror:holo_5",        "collection:holo5"]);
  if (hasJynxiHolo)         grants.push(["terror:jynxi_holo",    "collection:jynxiHolo"]);
  // ⭐ ALL FOUR, not three of four. cards.js carries exactly four rows
  // with an affinityType and AFFINITY_TYPES has four members, so
  // `>= 3` was awarding "Affinity Complete" — and a title reading
  // "Every Affinity, worn at least once" — to a player still missing
  // one. The id stays five_affinity; see the note in achievements.js
  // about why an achievement id is never renamed to fix a display bug.
  if (affinities.size >= 4) grants.push(["terror:five_affinity", "collection:affinity4"]);
  if (hasVoid)              grants.push(["terror:void_collector", "collection:voidPack"]);

  for (const [achId, source] of grants) {
    try { await grantAchievement(uid, achId, source); } catch (_) { /* non-fatal */ }
  }
};

// ── FOIL VAULT — buy a SPECIFIC card in a SPECIFIC holo tier ──
// The deterministic path: no gamble. Player pays the vault price and
// receives exactly the card + tier requested. Writes through the same
// append-style addCardToCollection (count ≥ 1, holoCount += 1) so it
// never overwrites or wipes existing collection data.
//
// Returns { success, reason?, cardId, holoTier }.
export const buyFoilSingle = async (uid, cardId, holoTier, cost) => {
  uid = _uidKey(uid, "buyFoilSingle");
  if (!uid) return { success: false, reason: "Invalid account." };
  if (!cardId || !holoTier) return { success: false, reason: "Invalid foil." };
  // ◆ THE FLIP LANDED, 2026-08-18. The TODO that stood here said the
  // Vault was "EVENTUALLY priced in Divine Crests, one-per-account" per
  // TERROR_CURRENCY_RESTRUCTURE_v1 §7 — and it was already TRUE of the
  // price table: FOIL_VAULT_PRICE in storyData.js has been denominated
  // in ◆ (6/9/11/12/13/13/15/18, +4 marquee) since the day it was
  // written. Only the DEBIT was temporarily pointed at ◕. So this is
  // not a repricing; it is the numbers finally being charged in the
  // currency they were always written in.
  //
  // Against THE CREST UNIT (◆1 = a quarter of a focused hour) a mythic
  // marquee at ◆22 is about five and a half hours. That is a chase
  // price and it should feel like one.

  // ── ONE PER ACCOUNT — THE MYTHICAL LAW, enforced at the WRITE ──
  // "DC is grindable via the Well, so DC-bought Mythicals are
  // one-per-account. DC is the key, not the supply." The key being
  // farmable is exactly why the door has to count.
  //
  // Checked BEFORE the debit, and checked against the vault's own
  // history rather than the collection — a player may legitimately own
  // this foil from a pack pull, and the Vault's promise is one
  // *purchase*, not one copy. The floor lives here, at the write, so a
  // stale shelf can never sell a second one.
  const vaultKey = `${cardId}~${holoTier}`;
  const already = await get(ref(db, `profiles/${uid}/foilVaultOwned/${vaultKey}`));
  if (already.exists()) {
    return { success: false, reason: "The Vault sells this one once per account. You already hold it." };
  }

  const spent = await spendDivineCrests(uid, cost);
  if (!spent) return { success: false, reason: "Not enough Divine Crests." };

  // GRANT BEFORE FLAG, and the flag is also the one-per-account seal.
  // A crash between them costs the player a second purchase they have
  // already been charged for once — recoverable and rare. The reverse
  // would seal a card they never received.
  await addCardToCollection(uid, cardId, true, holoTier, "vault");
  await runTransaction(
    ref(db, `profiles/${uid}/foilVaultOwned/${vaultKey}`),
    (current) => current || { at: Date.now(), paidDC: cost }
  );
  // Audit trail — same shape as packHistory, so a future hardening pass
  // can reconcile every grant.
  await update(ref(db, `profiles/${uid}`), {
    [`foilVaultHistory/${Date.now()}`]: { cardId, holoTier, cost, currency: "DC", paidDC: cost },
  });
  return { success: true, cardId, holoTier };
};

// ── PACK OPENING ──────────────────────────────────────────────

const pickRandom = (arr) => arr[Math.floor(Math.random() * arr.length)];

// Maps a packType to its provenance sigil id (see SourceSigil.SOURCE_SIGILS).
// EVERY pack now has its own named sigil ("every source" pass, Jul 2026) —
// a card pulled from any pack wears that pack's mark forever. The generic
// "booster" stamp remains only as the fallback for unknown/future packTypes
// (and as the legacy mark on cards pulled before this map was complete).
// Adding a pack = one SOURCE_SIGILS entry + one line here.
const PACK_SOURCE = {
  // Core seven
  void_eclipse:                 "booster_void_eclipse",
  solar_ascension:              "booster_solar_ascension",
  clockwork_dominion:           "booster_clockwork_dominion",
  dragons_wrath:                "booster_dragons_wrath",
  supreme_council:              "booster_supreme_council",
  instinct_rising:              "booster_instinct_rising",
  unstable_world:               "booster_unstable_world",
  // Holo editions
  void_eclipse_mythic:          "booster_void_eclipse_mythic",
  solar_ascension_gold:         "booster_solar_ascension_gold",
  clockwork_dominion_depths:    "booster_clockwork_dominion_depths",
  dragons_wrath_solrend:        "booster_dragons_wrath_solrend",
  supreme_council_aeterni:      "booster_supreme_council_aeterni",
  instinct_rising_shadow:       "booster_shadowfox", // the OG named sigil (PoC) — keeps legacy data coherent
  void_eclipse_void:            "booster_void_eclipse_void",
  unstable_world_jynxi:         "booster_unstable_world_jynxi",
  // ══ THE CORE — SCHOOL, TOMES AND SUPPORT ALL WEAR ONE MARK ══════
  // ⭐ caP'n, 2026-08-22: *"for all cards in that section they must have
  // the unique 'Core' sigil, only one, no holo variations etc — just the
  // one single Core sigil for every pack that exists or will exist in
  // Class, Tomes, Support (and if we add more, those too)."*
  //
  // ⚠ THESE ROWS ARE NOT TYPED — they are DERIVED from CORE_DRAWERS by
  // the spread below, and that is the whole guarantee. A hand-typed list
  // is a list someone forgets to add the fourteenth pack to, and the
  // fourteenth pack then quietly wears the generic `booster` envelope
  // and is not Core at all. Derived, a new id in CORE_DRAWERS.ids IS a
  // new row here, permanently, with no second edit to remember.
  //
  // The thirteen bespoke `booster_*` recipes these ids used to point at
  // are NOT deleted — see THE SHELVED SIGILS banner in SourceSigil.js.
  // They are authored and waiting to be relocated to other ways of
  // earning them.
  ...Object.fromEntries(Object.keys(CORE_DRAWER_OF).map(id => [id, "core"])),
  // Premier
  all_stars_mythic:             "booster_all_stars_mythic",
  depths_sanctuary:             "booster_depths_sanctuary",
  hybrid_chaos:                 "booster_hybrid_chaos",
  affinity_ascension:           "booster_affinity_ascension",
  prophecy_oracle:              "booster_prophecy_oracle",
  // Chaos
  randomizer_supreme:           "booster_randomizer_supreme",
};
const packSource = (packType) => PACK_SOURCE[packType] || "booster";
// ⭐ EXPORTED 2026-08-22 — the SHOP needs the same answer. caP'n asked
// for the pack's own Sigil on the shelf tile and in the detail header
// instead of a decorative glyph, and the only correct source for
// "which mark does this pack stamp on its cards" is the table the
// PULL already writes from. A second table in BoosterPack.js would
// have been the PACK_COSTS bug again.
export const getPackSourceId = (packType) => PACK_SOURCE[packType] || "booster";

// ── CANONICAL CARD ORIGINS ("where is this card FROM?") ──────────────
// Personal provenance (collection sources) answers "where did YOUR copy
// come from" — but it needs an owned record. This answers the universal
// question for ANY card, any viewer, any side of the board: which
// starter grimoires carry it, which pack pools drop it, and the Vault
// (which carries every card). Computed straight from game data, so it
// can never go stale and works for opponent cards we know nothing about.
// Holo Editions share their base pack's pool, so the base mark speaks
// for the whole lineage.
export function getCardOrigins(cardId) {
  if (!cardId) return [];
  // SEAL STRIP (July 28, 2026) — a Resurrected being arrives here carrying
  // its Necro Seal tag (spectral_tiger_lord~0~3). Every lookup below is an
  // exact key match against catalog data, so an unstripped id would miss
  // every starter deck and every pack pool and report the card as
  // Vault-only. Bare ids pass through untouched.
  cardId = baseId(cardId);
  const out = [];
  if (STARTER_DECKS.some(d => (d.grimoire && d.grimoire[cardId]) > 0)) out.push("starter");
  for (const [packId, pool] of Object.entries(PACK_POOLS)) {
    if (!pool) continue; // legacy id placeholders (e.g. phantom: null)
    if ((pool.common || []).includes(cardId) || (pool.rare || []).includes(cardId)) {
      out.push(PACK_SOURCE[packId] || "booster");
    }
  }
  out.push("vault"); // the Vault carries every card in the game
  return [...new Set(out)];
}

export const openPack = async (playerName, packType, isFree = false) => {
  playerName = _uidKey(playerName, "openPack");
  if (!playerName) return null;
  // Spend crests if not free
  if (!isFree) {
    // ═══════════════════════════════════════════════════════════
    // ⭐ ONE PRICE TABLE. THERE WERE TWO, AND THE SHOP READ THE
    //    OTHER ONE.
    // ═══════════════════════════════════════════════════════════
    // This line used to debit `PACK_COSTS[packType]`. The SHOP shows
    // `pack.cost` off BOOSTER_PACKS in storyData.js. Two tables, two
    // numbers, and nothing ever compared them — so for every one of
    // the 28 shop ids the shelf advertised one price and the till
    // charged another.
    //
    // The 2026-08-19 every-price-is-1 decree set BOOSTER_PACKS[].cost
    // to 1 across the board and never touched PACK_COSTS, which is how
    // this surfaced: a player with 3 coins sees "Purchase Pack — 1",
    // the button enables on `coins >= pack.cost`, they tap, and
    // all_stars_mythic quietly tries to take 20. A player with 300
    // coins is charged 20 for a pack the shelf priced at 1 and is
    // never told.
    //
    // ⚠ THIS DID NOT START ON 19 AUGUST. The decree only made the gap
    // visible. Before it, PACK_COSTS ran 5-20 while the shelf ran its
    // own numbers, and the `|| 300` fallback meant any pack id that was
    // ever renamed silently cost three hundred.
    //
    // THE FIX IS NOT TO SYNC THEM. Two tables that agree today are two
    // tables that disagree in six weeks. The shelf's number is the one
    // the player was shown and consented to, so the shelf's number is
    // the price, and PACK_COSTS is retired to a fallback for ids that
    // predate BOOSTER_PACKS.
    //
    // And the refusal said "Not enough Divine Crests" while debiting
    // Chocolate Coins — the wrong currency, on the one string a player
    // only ever sees when they are already frustrated.
    const shelf = BOOSTER_PACKS.find(bp => bp && bp.id === packType);
    const price = typeof shelf?.cost === "number" ? shelf.cost : (PACK_COSTS[packType] ?? 0);
    if (price > 0) {
      const spent = await spendCoins(playerName, price);
      if (!spent) return { success: false, reason: `Not enough Chocolate Coins — this pack costs ${price}.` };
    }
  }

  const isSpecial = packType === "special";
  
  // Determine if this is a boutique pack with guaranteed holos
  const BOUTIQUE_PACKS = {
    void_eclipse_mythic: ["holo_mythic", "holo_silver"],
    solar_ascension_gold: ["holo_gold", "holo_silver"],
    clockwork_dominion_depths: ["holo_depths"],
    dragons_wrath_solrend: ["holo_solrend"],
    supreme_council_aeterni: ["holo_aeternus"],
      void_eclipse_void: ["holo_void"],
    unstable_world_jynxi: ["holo_jynxi"],
    depths_sanctuary: ["holo_depths"],
    all_stars_mythic: ["holo_mythic"],
  };
  
  const isBoutique = BOUTIQUE_PACKS[packType];
  // ⭐ 2026-08-22 — ONE resolver, so a Tome pack deals three and a
  // Support pack deals four without this line knowing either number.
  const config = getPackContents(packType, { boutique: !!isBoutique, special: isSpecial });
  const pool = resolvePool(packType);
  const holoTierList = BOUTIQUE_PACKS[packType] || null;
  const src = packSource(packType); // provenance sigil for every card in this pack

  const cards = [];

  // Draw commons
  for (let i = 0; i < config.commons; i++) {
    const cardId = pickRandom(pool.common);
    const isHolo = isBoutique ? true : (!isSpecial && Math.random() < config.holoChance);
    const holoTier1 = isHolo ? (holoTierList ? rollExtendedHoloTier(holoTierList) : rollHoloTier()) : null;
    cards.push({ cardId, isHolo, holoTier: holoTier1, rarity: holoTier1 || RARITY.COMMON, source: src });
    await addCardToCollection(playerName, cardId, isHolo, holoTier1, src);
  }

  // Draw uncommons (from common pool, slightly better)
  for (let i = 0; i < (config.uncommons || 0); i++) {
    const cardId = pickRandom([...pool.common, ...pool.rare].slice(0, Math.ceil(pool.rare.length * 0.5)));
    const isHolo = isBoutique ? true : (!isSpecial && Math.random() < config.holoChance);
    const holoTier2 = isHolo ? (holoTierList ? rollExtendedHoloTier(holoTierList) : rollHoloTier()) : null;
    cards.push({ cardId, isHolo, holoTier: holoTier2, rarity: holoTier2 || RARITY.RARE, source: src });
    await addCardToCollection(playerName, cardId, isHolo, holoTier2, src);
  }

  // Draw rares
  for (let i = 0; i < config.rares; i++) {
    const cardId = pickRandom(pool.rare);
    const isHolo = isBoutique ? true : (!isSpecial && Math.random() < config.holoChance);
    const holoTierR = isHolo ? (holoTierList ? rollExtendedHoloTier(holoTierList) : rollHoloTier()) : null;
    cards.push({ cardId, isHolo, holoTier: holoTierR, rarity: holoTierR || RARITY.RARE, source: src });
    await addCardToCollection(playerName, cardId, isHolo, holoTierR, src);
  }

  // ═══════════════════════════════════════════════════════════
  // ⭐ THE TENTH CARD. Every pack advertised 10 and delivered 9.
  // ═══════════════════════════════════════════════════════════
  // The three loops above draw commons + uncommons + rares = 3+2+4 =
  // NINE. `config.total` said 10 and was never read anywhere in this
  // file — I grepped every `config.` reference and the only four are
  // commons, uncommons, rares and holoChance. The comment on
  // PACK_CONTENTS shows what was meant: "10 cards: 3 common, 2
  // uncommon, 4 rare, 1 HOLO SLOT (15% chance)". The holo slot became
  // a per-card 15% roll inside the other three loops, and the card it
  // was supposed to be was simply dropped.
  //
  // Both the pack preview and the confirm modal print "10 cards per
  // pack". The player paid for ten and received nine, every pack, in
  // every tier, since the shop shipped.
  //
  // Derived rather than hardcoded, so `total` is now LOAD-BEARING and
  // an unread number in that table can never hide this again:
  //   standard         10 - 9  - 0 = 1   ← the missing slot
  //   holo_guaranteed  10 - 9  - 0 = 1
  //   special          12 - 10 - 2 = 0   ← already correct, unchanged
  const declaredTotal = Number(config.total) || 0;
  const drawnSoFar = (config.commons || 0) + (config.uncommons || 0) + (config.rares || 0);
  const reservedGuaranteed = isSpecial ? (config.guaranteedHolos || 0) : 0;
  const slotCount = Math.max(0, declaredTotal - drawnSoFar - reservedGuaranteed);

  for (let i = 0; i < slotCount; i++) {
    const cardId = pickRandom(pool.rare);
    const isHolo = isBoutique ? true : (!isSpecial && Math.random() < config.holoChance);
    const holoTierS = isHolo ? (holoTierList ? rollExtendedHoloTier(holoTierList) : rollHoloTier()) : null;
    cards.push({ cardId, isHolo, holoTier: holoTierS, rarity: holoTierS || RARITY.RARE, source: src });
    await addCardToCollection(playerName, cardId, isHolo, holoTierS, src);
  }

  // Guaranteed holos for special packs
  if (isSpecial) {
    const holosNeeded = config.guaranteedHolos || 1;
    for (let i = 0; i < holosNeeded; i++) {
      const holoCard = pickRandom(pool.rare);
      cards.push({ cardId: holoCard, isHolo: true, holoTier: RARITY.HOLO_MYTHIC, rarity: RARITY.HOLO_MYTHIC, source: src });
      await addCardToCollection(playerName, holoCard, true, RARITY.HOLO_MYTHIC, src);
    }
  }

  // Track pack opening
  await update(ref(db, `profiles/${playerName}`), {
    [`packHistory/${Date.now()}`]: { packType, cards: cards.map(c => c.cardId) },
  });

  // Achievements: first pack ever, and first holo
  const profileRef = ref(db, `profiles/${playerName}`);
  const profileSnap = await get(profileRef);
  const profile = profileSnap.val() || {};
  const packHistoryCount = Object.keys(profile.packHistory || {}).length;
  
  if (packHistoryCount === 1) {
    await grantAchievement(playerName, "terror:first_pack", "boutique:openPack");
  }
  
  // First holo check: if any card in this pack is holo, grant first_holo if earned
  const hasHolo = cards.some(c => c.isHolo);
  if (hasHolo) {
    await grantAchievement(playerName, "terror:first_holo", "boutique:openPack");
  }

  return { success: true, cards };
};

// ── SPECIAL DUNGEON PACK ──────────────────────────────────────
// Awarded when a dungeon is cleared — includes guaranteed holo mascot

export const openDungeonPack = async (playerName, dungeonId, starterDeckId) => {
  playerName = _uidKey(playerName, "openDungeonPack");
  if (!playerName) return null;
  const pool = PACK_POOLS.space; // mixed pool for dungeon packs
  const allPool = [
    ...resolvePool('solar_ascension').common, ...resolvePool('solar_ascension').rare,
    ...resolvePool('void_eclipse').common, ...resolvePool('void_eclipse').rare,
    ...resolvePool('clockwork_dominion').common, ...resolvePool('clockwork_dominion').rare,
    ...resolvePool('dragons_wrath').common, ...resolvePool('dragons_wrath').rare,
  ];

  const cards = [];

  // 7 random cards from all pools
  for (let i = 0; i < 7; i++) {
    const cardId = pickRandom(allPool);
    const isHolo = Math.random() < 0.2;
    cards.push({ cardId, isHolo, rarity: isHolo ? RARITY.HOLO : RARITY.RARE, source: "earned" });
    await addCardToCollection(playerName, cardId, isHolo, isHolo ? rollHoloTier() : null, "earned");
  }

  // Guaranteed holo mascot based on starter deck
  const mascots = DUNGEON_HOLO_MASCOTS[`dungeon_${dungeonId}`] || {};
  const deckKey = starterDeckId?.replace(/_deck$/, "").replace(/_/g, "_") || "h01";
  const mascotId = mascots[deckKey] || mascots["h01"] || "h01_lord";
  cards.push({ cardId: mascotId, isHolo: true, rarity: RARITY.HOLO, isMascot: true, source: "earned" });
  await addCardToCollection(playerName, mascotId, true, null, "earned");

  return { success: true, cards };
};

// ── STORY PROGRESS ────────────────────────────────────────────

export const recordStoryWin = async (playerName, opponentId, dungeonId) => {
  playerName = _uidKey(playerName, "recordStoryWin");
  if (!playerName) return;
  const profileRef = ref(db, `profiles/${playerName}`);
  const snap = await get(profileRef);
  if (!snap.exists()) return;
  const profile = snap.val();

  const defeated = profile.storyProgress?.defeatedOpponents || [];
  const alreadyDefeated = defeated.includes(opponentId);
  const newDefeated = [...new Set([...defeated, opponentId])];

  const updates = {
    totalWins: (profile.totalWins || 0) + 1,
    "storyProgress/defeatedOpponents": newDefeated,
  };

  // Award crests.
  // Rematch rule (D1 completion wave): re-defeating an opponent the
  // player has ALREADY beaten pays STORY_REMATCH ("almost nothing") —
  // dungeons stay re-enterable without becoming a crest farm.
  let crests;
  if (alreadyDefeated) {
    crests = COIN_REWARDS.STORY_REMATCH;
  } else {
    crests = COIN_REWARDS.NPC_WIN + COIN_REWARDS.FIRST_DEFEAT_BONUS;

    // Mid-dungeon bonus — awarded when this FIRST-defeat brings the
    // player to exactly 2 downed opponents in this dungeon. (This was
    // documented in recordDungeonClear's comment but never implemented —
    // wired for real in the D1 completion wave.)
    if (dungeonId != null) {
      try {
        const { OPPONENTS } = await import("./storyData");
        const inDungeon = Object.values(OPPONENTS)
          .filter(o => o.dungeonId === dungeonId)
          .map(o => o.id);
        const downedHere = inDungeon.filter(id => newDefeated.includes(id)).length;
        if (downedHere === 2) crests += COIN_REWARDS.MID_DUNGEON_BONUS;
      } catch (_) { /* non-fatal — bonus simply skipped */ }
    }
  }

  await update(profileRef, updates);

  // ◕ Payout on its own delta transaction — never folded into the profile
  // update above. A read-then-write on a balance is the exact pattern that
  // caused the April 2026 data-loss incident; awardCoins is the safe lane.
  await awardCoins(playerName, crests, "storyWin");

  // Mirror to public leaderboard (name + new win total)
  await mirrorLeaderboard(playerName, profile.name, (profile.totalWins || 0) + 1);

  // First blood achievement
  if ((profile.totalWins || 0) === 0) {
    await grantAchievement(playerName, "terror:first_blood", "storyMode:win");
  }

  return { crests, firstDefeat: !alreadyDefeated };
};

export const recordDungeonClear = async (playerName, dungeonId) => {
  playerName = _uidKey(playerName, "recordDungeonClear");
  if (!playerName) return;
  const profileRef = ref(db, `profiles/${playerName}`);
  const snap = await get(profileRef);
  if (!snap.exists()) return;
  const profile = snap.val();

  const completed = profile.storyProgress?.completedDungeons || [];
  if (completed.includes(dungeonId)) return { alreadyCleared: true };

  await update(profileRef, {
    "storyProgress/completedDungeons": [...completed, dungeonId],
    "storyProgress/currentDungeon": dungeonId + 1,
    // Note: mid-dungeon bonus (after 2nd opponent) is awarded in recordStoryWin
  });
  await awardCoins(playerName, COIN_REWARDS.STORY_CHAPTER_CLEAR, "dungeonClear");

  // First dungeon clear
  if (completed.length === 0) {
    await grantAchievement(playerName, "terror:dungeon_clear", "storyMode:dungeonClear");
  }

  return { alreadyCleared: false, crests: COIN_REWARDS.STORY_CHAPTER_CLEAR, coins: COIN_REWARDS.STORY_CHAPTER_CLEAR };
};

// ═══════════════════════════════════════════════════════════════
// DUNGEON SECRETS — the persistent "easter eggs found" truth
// ───────────────────────────────────────────────────────────────
// Every collectible secret in a dungeon (crest caches, chests,
// grimoire pages, the Oath Tablet) is stamped to Firebase at
// `profiles/{uid}/storyProgress/dungeonSecrets/d{dungeonId}/{secretId}`.
// This node is the SOURCE OF TRUTH the dungeon HUD counter,
// reward grants, and the all-secrets achievement derive from —
// it survives localStorage wipes and device changes, unlike the
// dungeon's local run state.
//
// Write pattern: runTransaction on the exact stamp path — atomic
// idempotence. A stamp that already exists ABORTS the transaction,
// so crests/cards can never be double-granted even on a double-tap.
// Append-only: stamps are never deleted.
//
// ⚠ RULES-HARDENING NOTE: whitelist
//   `storyProgress/dungeonSecrets/$d/$secretId`
// under profiles when the profile node gets its $extra:false pass.
// ═══════════════════════════════════════════════════════════════

// Canonical secret roster per dungeon — the HUD "✶ X/Y" counter and
// the all-secrets achievement both count against THIS list. Trimming
// what counts as an "egg" is a one-line edit here.
export const DUNGEON_SECRET_IDS = {
  1: [
    "veil_cache",         // B1F hidden crest stash
    "veil_chest",         // B1F dead-end chest
    "grimoire_page_1",    // B2F hidden page
    "drift_secret_chest", // B2F secret room — S'uhnker Story Variant
    "grimoire_page_2",    // SANCTUM hidden page
    "sanctum_chest",      // SANCTUM post-boss Warden's Chest
    "easter_egg_tablet",  // Oath Tablet — Chaos Dragon Easter Egg Variant
  ],
};

// Record a found secret. Grants its rewards exactly once, ever.
//   opts.crests — Chocolate Coins to award (delta transaction via awardCoins)
//   opts.card   — { id, holoTier } to grant via addCardToCollection
// Returns { alreadyFound } (or undefined on bad uid).
export const recordDungeonSecret = async (uid, dungeonId, secretId, opts = {}) => {
  uid = _uidKey(uid, "recordDungeonSecret");
  if (!uid || !secretId || dungeonId == null) return;
  const { crests = 0, card = null } = opts;

  const stampRef = ref(db, `profiles/${uid}/storyProgress/dungeonSecrets/d${dungeonId}/${secretId}`);
  const result = await runTransaction(stampRef, (current) =>
    current ? undefined : Date.now()   // undefined = abort (already stamped)
  );
  if (!result.committed) return { alreadyFound: true };

  // Rewards — first find only (guaranteed by the transaction above).
  if (crests > 0) {
    await awardCoins(uid, crests, `dungeon${dungeonId}:${secretId}`);
  }
  if (card?.id) {
    await addCardToCollection(uid, card.id, !!card.holoTier, card.holoTier || null,
      `dungeon${dungeonId}:${secretId}`);
  }

  // Oath Tablet — hidden achievement.
  if (secretId === "easter_egg_tablet") {
    try { await grantAchievement(uid, "terror:oath_tablet", `dungeon${dungeonId}:tablet`); }
    catch (_) { /* non-fatal */ }
  }

  // All-secrets achievement — evaluated off the fresh stamp map.
  const roster = DUNGEON_SECRET_IDS[dungeonId] || [];
  if (roster.length) {
    try {
      const snap = await get(ref(db, `profiles/${uid}/storyProgress/dungeonSecrets/d${dungeonId}`));
      const map = snap.exists() ? snap.val() : {};
      if (roster.every(id => map[id])) {
        await grantAchievement(uid, `terror:d${dungeonId}_all_secrets`, "dungeon:allSecrets");
      }
    } catch (_) { /* non-fatal */ }
  }

  return { alreadyFound: false };
};

export const recordJynxiDefeat = async (playerName) => {
  playerName = _uidKey(playerName, "recordJynxiDefeat");
  if (!playerName) return;
  const profileRef = ref(db, `profiles/${playerName}`);
  const snap = await get(profileRef);
  if (!snap.exists()) return;
  const profile = snap.val();
  if (profile.storyProgress?.jynxiDefeated) return { alreadyDefeated: true };
  await update(profileRef, {
    "storyProgress/jynxiDefeated": true,
  });
  await awardCoins(playerName, COIN_REWARDS.STORY_COMPLETE, "jynxiDefeat");
  
  // Story complete achievement + beat J'ynxi
  await grantAchievement(playerName, "terror:beat_jynxi", "storyMode:jynxiDefeat");
  await grantAchievement(playerName, "terror:story_complete", "storyMode:jynxiDefeat");
  
  return { crests: COIN_REWARDS.STORY_COMPLETE, coins: COIN_REWARDS.STORY_COMPLETE };
};

// Records a pure free-play (NPC) win. Optional `starterDeckId`: when the
// win was played with a PRESET starter deck (Plaza's starter chooser
// threads it through the match meta), the deck is stamped into an
// append-only `starterWins/{deckId}: true` map on the profile — the
// tracker behind terror:all_starters. ⚠ IT GRANTS ON FOUR WINS, NOT
// TWELVE, and this comment said twelve until 2026-09-14: the code below
// asks for one stamped deck of each SCHOOL (caP'n's July 3 ruling —
// "the four starter decks" means the four deck TYPES), and
// achievements.js's description says the same. There are twelve decks,
// three per School, so the per-deck stamps are richer than the
// achievement needs and stay that way for a future "all 12" tier.
//
// ⭐ RE-CONFIRMED 2026-09-15. caP'n was asked straight out whether
// Starter Master is four wins or twelve. He said: *"4"*. NOTHING WAS
// CHANGED BY THAT RULING, because nothing was wrong: the code already
// asked for four, achievements.js already described four, and no
// literal twelve survives anywhere as a threshold — the only twelves
// in this file are the true statement that twelve DECKS exist. It is
// written down here because the question has now been asked twice, a
// year apart, and the next reader should find the answer instead of
// re-deriving it. FOUR IS THE COUNT OF SCHOOLS, NOT A TARGET.
// Exact-child-path write, never a
// full-map overwrite; delta pattern per the Firebase discipline.
// NOTE for the rules-hardening session: whitelist `starterWins/$deckId`
// under profiles when the profile node gets its $extra:false pass.
// ═════════════════════════════════════════════════════════════
// ⭐ FIRST BLOOD — "Win your very first match in Terror."
// ═════════════════════════════════════════════════════════════
// The most basic achievement in the game, and NOBODY COULD EARN IT.
//
// Its only grant lived inside `recordStoryWin`, which has exactly one
// caller — App.js, guarded on `meta.storyOpponentId`, which is set in
// exactly one place: the `screen === "dungeon1"` branch. Dungeon 1
// ships asleep by caP'n's decree and has no route in, so that branch
// is unreachable, so recordStoryWin never runs. Every other win path
// walked straight past it: world-map clears go through recordNodeClear,
// free play through recordFreePlayWin, the Depths through
// recordDepthResult.
//
// So it is a HELPER now, called from every path that records a win,
// and it asks the only question the achievement's own text asks: is
// this your first? grantAchievement is idempotent, so calling it from
// four places is free and forgetting one place is the only real risk.
//
// ⚠ IF YOU ADD A FIFTH WAY TO WIN A SOLREND, CALL THIS FROM IT.
export const grantFirstBlood = async (uid, source) => {
  if (!uid) return;
  try { await grantAchievement(uid, "terror:first_blood", source || "solrend:win"); }
  catch (_) { /* non-fatal — never let a badge cost somebody their win */ }
};

export const recordFreePlayWin = async (playerName, starterDeckId = null) => {
  playerName = _uidKey(playerName, "recordFreePlayWin");
  if (!playerName) return;
  const profileRef = ref(db, `profiles/${playerName}`);
  const snap = await get(profileRef);
  if (!snap.exists()) return;
  const profile = snap.val();

  const isFirst = (profile.totalWins || 0) === 0;
  const crests = COIN_REWARDS.NPC_WIN + (isFirst ? COIN_REWARDS.FIRST_WIN_BONUS : 0);
  const freePlayWins = (profile.freePlayWins || 0) + 1;

  await update(profileRef, {
    totalWins: (profile.totalWins || 0) + 1,
    freePlayWins,
  });
  await awardCoins(playerName, crests, "freePlayWin");

  // Mirror to public leaderboard (name + new win total)
  await mirrorLeaderboard(playerName, profile.name, (profile.totalWins || 0) + 1);

  // FIRST BLOOD — any first win in the game, not only a story one.
  if (isFirst) await grantFirstBlood(playerName, "freePlay:win");

  // First freeplay win
  if (freePlayWins === 1) {
    await grantAchievement(playerName, "terror:first_freeplay", "freePlay:win");
  }
  // 50 free-play wins — the Free Spirit. Idempotent.
  if (freePlayWins >= 50) {
    try { await grantAchievement(playerName, "terror:freeplay_50", "freePlay:win50"); }
    catch (_) { /* non-fatal */ }
  }

  // ── Starter Master tracker (terror:all_starters) ─────────────
  // Stamp the exact deck's win (append-only), then evaluate completion
  // per caP'n's canon ruling (July 3): "the four starter decks" means
  // the four starter deck TYPES — the SCHOOLS. There are 12 preset
  // decks, 3 per School (Space / Phantom / Time / Dragon); the
  // achievement grants once the player has a win with at least one deck
  // OF EACH SCHOOL.
  // ⚠ SCHOOL, NOT ILK, AND THE WORD MATTERS. Space/Phantom/Time/Dragon
  // is the TOP-RIGHT stamp — the School. The Ilk is the top-left stamp
  // (Humanoid/Beast/Machine/Dragon) and no starter deck carries one.
  // This comment and the field it describes both said "Ilk" until
  // 2026-09-14 (caP'n: "school :3").
  // Per-deck stamps are kept (richer data than per-School — future-proof
  // for a "win with all 12" prestige tier if wanted). grantAchievement
  // is idempotent, so re-evaluating on every stamped win is safe.
  //
  // ⚠ THE FOUR IS DERIVED, NOT WRITTEN. `allSchools` is built from the
  // table, so the bar is "one deck of every School that exists" — today
  // exactly four (Space / Phantom / Time / Dragon, three decks each).
  // That is deliberate: authoring a thirteenth deck in an EXISTING
  // School costs nothing. But a deck in a FIFTH School would silently
  // move the achievement to five wins, and achievements.js's
  // description names the four Schools by hand and would go stale. If a
  // fifth School is ever authored, both places need caP'n's word.
  if (starterDeckId && STARTER_DECKS.some(d => d.id === starterDeckId)) {
    try {
      await update(ref(db, `profiles/${playerName}/starterWins`), {
        [starterDeckId]: true,
      });
      const stamped = { ...(profile.starterWins || {}), [starterDeckId]: true };
      const schoolsWon = new Set(
        STARTER_DECKS.filter(d => stamped[d.id]).map(d => d.school)
      );
      const allSchools = new Set(STARTER_DECKS.map(d => d.school));
      const allWon = [...allSchools].every(school => schoolsWon.has(school));
      if (allWon) {
        await grantAchievement(playerName, "terror:all_starters", "freePlay:allStarters");
      }
    } catch (_) { /* non-fatal — never break a recorded win */ }
  }

  return { crests, isFirstWin: isFirst };
};

// ─────────────────────────────────────────────────────────────
// PVP RESULT — C8 (July 17 2026). The human-vs-human endgame
// finally writes something. Each client records its OWN result
// (winner and loser both call this for their own uid — no client
// ever writes another player's profile, per the Profile-Key Seal).
//
// Pays: PVP_WIN / PVP_LOSS crests (delta on the live balance).
// Ladder: pvp/score moves by PVP_SCORE deltas, floored at 0;
//         pvp/peakScore is high-water (rank once earned is earned —
//         same philosophy as Depths peakElo).
// Wins bump totalWins → PvP victories count toward the Nullus/Omnis
// faction "Win N Solrends" challenges for free (a Solrend is a
// Solrend) and mirror to the public leaderboard.
//
// FIREBASE NOTE (C3 ledger): new node `profiles/$uid/pvp` with
// children wins/losses/score/peakScore/lastAt — whitelist in
// rules.json BEFORE this code deploys (rules first: additive).
// ─────────────────────────────────────────────────────────────
// opts.conceded (C16, July 17 2026): a concede records as a full LOSS
// on the ladder (−12, same as losing honestly — quitting is not an
// escape hatch from the score) but pays ZERO crests: the participation
// crest is for participating, and two coordinated players insta-
// conceding back and forth must never out-earn actually playing.
export const recordPvpResult = async (playerName, won, opts = {}) => {
  playerName = _uidKey(playerName, "recordPvpResult");
  if (!playerName) return null;
  const profileRef = ref(db, `profiles/${playerName}`);
  const snap = await get(profileRef);
  if (!snap.exists()) return null;
  const profile = snap.val();

  const pvp    = profile.pvp || {};
  const wins   = (pvp.wins   || 0) + (won ? 1 : 0);
  const losses = (pvp.losses || 0) + (won ? 0 : 1);
  const delta  = won ? PVP_SCORE.WIN : PVP_SCORE.LOSS;
  const score  = Math.max(0, (pvp.score || 0) + delta);
  const peak   = Math.max(score, pvp.peakScore || 0);
  const crests = won ? COIN_REWARDS.PVP_WIN
                     : (opts.conceded ? 0 : COIN_REWARDS.PVP_LOSS);

  const updates = {
    "pvp/wins":      wins,
    "pvp/losses":    losses,
    "pvp/score":     score,
    "pvp/peakScore": peak,
    "pvp/lastAt":    Date.now(),
  };
  if (won) updates.totalWins = (profile.totalWins || 0) + 1;
  await update(profileRef, updates);
  if (crests > 0) await awardCoins(playerName, crests, won ? "pvpWin" : "pvpLoss");

  // ── T16: mirror the ladder score to the world-readable board ──
  // Win OR loss — the score moved either way. update() merges, so
  // this never clobbers the wins mirror. Non-blocking, like its
  // sibling. FIREBASE NOTE (C3): whitelist `leaderboard/$uid/pvpScore`.
  try {
    await update(ref(db, `leaderboard/${playerName}`), {
      name: profile.name || "Aenarchist",
      pvpScore: score,
      updatedAt: Date.now(),
    });
  } catch (e) { console.warn("[pvpScore mirror] failed:", e); }

  if (won) {
    // FIRST BLOOD — see grantFirstBlood. A duel is a duel.
    if ((profile.totalWins || 0) === 0) await grantFirstBlood(playerName, "pvp:win");
    await mirrorLeaderboard(playerName, profile.name, (profile.totalWins || 0) + 1);
    if (wins === 1) {
      try { await grantAchievement(playerName, "terror:first_pvp", "pvp:firstWin"); }
      catch (_) { /* non-fatal */ }
    }
    if (wins >= 10) {
      try { await grantAchievement(playerName, "terror:pvp_10", "pvp:win10"); }
      catch (_) { /* non-fatal */ }
    }
  }

  return { crests, scoreDelta: delta, newScore: score, peakScore: peak, wins, losses };
};

// ─────────────────────────────────────────────────────────────
// JESTER MATCH STATS — C7 (July 17 2026). The three J'ynxi's
// Jesters in-match predicates finally have a write path. Called
// fire-and-forget from Shatterfield's match-end effect with the
// stats its in-match tracker counted.
//
// SEMANTICS (matched to FactionHall's claim gate, which compares
// the raw member-row counter against challenge.goal):
//   jesters_1 — "Activate 5 Nihil tomes in one Solrend"
//               → counter is the BEST single-Solrend Nihil count
//                 (high-water; one 5-Nihil game completes it).
//   jesters_2 — "Play both Nullus & Omnis tomes in one game" ×3
//               → +1 per Solrend where ≥1 Nihil AND ≥1 Omnia
//                 tome both resolved.
//   jesters_3 — "Win 5 Solrends using only Ancient Sorcery tomes"
//               → +1 per WON Solrend where every resolved tome
//                 was Ancient Sorcery ilk (and at least one was —
//                 a tome-less win is not "using" them).
//
// Membership-gated: if the uid has no Jesters member row, this is
// a silent no-op — instrumentation never implicitly enrolls anyone.
//
// FIREBASE NOTE (C3 ledger): new numeric children jesters_1/2/3 on
// `factions/jesters/members/$uid` — whitelist alongside the existing
// *_claimed flags BEFORE this code deploys.
// ─────────────────────────────────────────────────────────────
export const recordJesterMatchStats = async (playerName, stats = {}) => {
  playerName = _uidKey(playerName, "recordJesterMatchStats");
  if (!playerName) return;
  try {
    const memberRef = ref(db, `factions/jesters/members/${playerName}`);
    const snap = await get(memberRef);
    if (!snap.exists()) return; // not a Jester — never implicitly enroll
    const row = snap.val() || {};

    const updates = {};
    const nihil = stats.nihilTomes || 0;
    if (nihil > (row.jesters_1 || 0)) updates.jesters_1 = nihil;
    if (stats.playedBothSides)        updates.jesters_2 = (row.jesters_2 || 0) + 1;
    if (stats.wonAncientOnly)         updates.jesters_3 = (row.jesters_3 || 0) + 1;

    if (Object.keys(updates).length) await update(memberRef, updates);
  } catch (e) {
    console.warn("[recordJesterMatchStats] failed:", e); // never break a match exit
  }
};

// ─────────────────────────────────────────────────────────────
// ENDLESS DEPTHS — ELO & DEPTH PROGRESS
// ─────────────────────────────────────────────────────────────

export const DEPTH_ELO_CONFIG = {
  BASE_WIN:    28,
  BASE_LOSS:   18,
  DRAW:         5,
  DIFF_MULT: { Beb: 0.8, Champion: 1.0, Lord: 1.3 },
};

export async function loadDepthProfile(uid) {
  const snap = await get(ref(db, `profiles/${uid}/endlessDepths`));
  return snap.exists() ? snap.val() : {
    elo: 0, peakElo: 0, wins: 0, losses: 0, draws: 0,
    streak: 0, bestStreak: 0, defeatedGatekeepers: [], history: [],
  };
}

export async function recordDepthResult(uid, result, opponentName, isGatekeeper, opponentDifficulty, ctx = {}) {
  const depthRef = ref(db, `profiles/${uid}/endlessDepths`);
  const snap = await get(depthRef);
  const current = snap.exists() ? snap.val() : {};

  const mult = DEPTH_ELO_CONFIG.DIFF_MULT[opponentDifficulty] || 1;
  const delta = result === "win"  ?  Math.round(DEPTH_ELO_CONFIG.BASE_WIN  * mult)
              : result === "loss" ? -Math.round(DEPTH_ELO_CONFIG.BASE_LOSS * mult)
              : -DEPTH_ELO_CONFIG.DRAW;

  const newElo    = Math.max(0, (current.elo || 0) + delta);
  const newPeak   = Math.max(newElo, current.peakElo || 0);
  const newStreak = result === "win" ? (current.streak || 0) + 1 : 0;

  const entry = {
    t: Date.now(), result, opponentName, eloChange: delta, newElo, isGatekeeper: !!isGatekeeper,
  };

  await update(ref(db, `profiles/${uid}/endlessDepths`), {
    elo: newElo, peakElo: newPeak,
    wins:    (current.wins || 0)   + (result === "win"  ? 1 : 0),
    losses:  (current.losses || 0) + (result === "loss" ? 1 : 0),
    draws:   (current.draws || 0)  + (result === "draw" ? 1 : 0),
    streak:  newStreak,
    bestStreak: Math.max(newStreak, current.bestStreak || 0),
    history: [entry, ...(current.history || [])].slice(0, 50),
  });

  // ── ◆ THE INNER EMPERIUM LADDER FINALLY MOVES ────────────────
  // (The Totem's half. The named-villain half is recordInnerWin, at
  //  the bottom of this file — same counter, two doors into it.)
  // `innerWinsRequired` sits on eight villains in storyData and had
  // EIGHT occurrences in the whole repo — all of them definitions.
  // `profile.innerWins` was read twice, rendered in the Inner Emperium
  // HUD as "{n} victories recorded within", and WRITTEN BY NOTHING. So
  // the counter was permanently 0, the ladder had no gate, and the
  // screen told every player the same lie forever.
  //
  // caP'n, 2026-08-20: *"Inner Emperium Ladder — you mean the Totem?
  // wire it if so."* It is. The Totem IS the Inner Emperium: the
  // Gauntlet, the Depths, the endless bouts on one pool of Anima. So a
  // win down there is a victory recorded WITHIN, and this is the one
  // function every one of them already passes through.
  //
  // ⚠ A SEPARATE COUNTER FROM endlessDepths.wins ON PURPOSE. That one
  // is the ELO record and it is reset-shaped — it belongs to a ladder
  // that can be climbed and fallen down. `innerWins` is a lifetime
  // count on the PROFILE, beside totalWins, because the villain gates
  // are permanent unlocks: reaching Kel'dara at 15 and then losing
  // streak must not re-lock her.
  //
  // Non-fatal and delta-only: never a read-then-write of the profile
  // object (the write pattern behind the April 2026 data-loss).
  if (result === "win") {
    try {
      await runTransaction(ref(db, `profiles/${uid}/innerWins`), (cur) => (cur || 0) + 1);
    } catch (e) { console.warn("[innerWins] bump failed:", e && e.message); }
  }

  // What this result actually granted — returned so the UI can
  // celebrate drops without re-deriving them.
  const rewards = { trophyCard: null, streakDrops: [], title: null };

  // ── Depths achievements & reward grants ──────────────────────
  // Rank achievements key off PEAK elo (a rank, once reached, is earned
  // for good even if elo later dips). Win-count + streak milestones key
  // off the running totals. All idempotent via grantAchievement /
  // streakMilestonesHit / defeatedGatekeepers first-time checks.
  try {
    const totalWins = (current.wins || 0) + (result === "win" ? 1 : 0);
    // FIRST BLOOD — see grantFirstBlood. The Depths are a first duel
    // for plenty of players; the achievement's text says "your very
    // first match", not "your very first story match".
    if (result === "win") await grantFirstBlood(uid, "depths:win");
    if (newPeak >= 800)  await grantAchievement(uid, "terror:depths_champion",   "depths:rank");
    if (newPeak >= 2200) await grantAchievement(uid, "terror:depths_aenarchist", "depths:rank");
    if (newPeak >= 2800) await grantAchievement(uid, "terror:depths_solrend",    "depths:rank");
    if (totalWins >= 200) await grantAchievement(uid, "terror:depths_200_wins",  "depths:winCount");
    if (totalWins >= 500) await grantAchievement(uid, "terror:depths_500_wins",  "depths:winCount");
    if (newStreak >= 50)  await grantAchievement(uid, "terror:streak_50",        "depths:streak");

    // ── Gatekeeper defeat + TROPHY DROP ─────────────────────────
    // The live path runs through here (the standalone
    // recordGatekeeperDefeat is not wired into the UI). On a
    // gatekeeper WIN we persist the defeat, fire the achievement,
    // and — on FIRST defeat only — drop the trophy card in the
    // canonical holo_depths foil. Detection handles both legacy
    // ("gk_suhnker") and short ("suhnker") id formats.
    if (isGatekeeper && result === "win") {
      const gkId = ctx.gatekeeperId || opponentName;
      const prevGks = current.defeatedGatekeepers || [];
      const shortId = String(gkId).replace(/^gk_/, "");
      const firstDefeat = !prevGks.includes(gkId) && !prevGks.includes(shortId) && !prevGks.includes(`gk_${shortId}`);
      const updatedGks = [...new Set([...prevGks, gkId])];
      await update(ref(db, `profiles/${uid}/endlessDepths`), { defeatedGatekeepers: updatedGks });
      await grantAchievement(uid, "terror:gatekeeper_first", "depths:gatekeeper");
      if (firstDefeat) {
        const trophy = GATEKEEPER_TROPHIES[shortId];
        if (trophy?.trophyCardId) {
          rewards.trophyCard = trophy.trophyCardId;
          await addCardToCollection(uid, trophy.trophyCardId, true, RARITY.HOLO_DEPTHS, "depths:gatekeeper");
        }
      }
    }

    // ── STREAK MILESTONE DROPS ──────────────────────────────────
    // Crossing a threshold fires exactly once per profile (tracked in
    // streakMilestonesHit). Card milestones drop a random Escendant of
    // the pool's level in holo_depths; the 50 milestone grants the
    // "the Unbroken" profile title cosmetic instead.
    if (result === "win") {
      const hit = current.streakMilestonesHit || [];
      const newlyHit = STREAK_THRESHOLDS.filter(t => newStreak >= t.at && !hit.includes(`s${t.at}`));
      if (newlyHit.length > 0) {
        const updatedHits = [...hit, ...newlyHit.map(t => `s${t.at}`)];
        await update(ref(db, `profiles/${uid}/endlessDepths`), { streakMilestonesHit: updatedHits });
        for (const t of newlyHit) {
          const filter = STREAK_POOL_FILTERS[t.pool];
          if (filter?.titleOnly) {
            await grantCosmetic(uid, "title_the_unbroken", {
              source: "terror:depths_streak50", rarity: "mythic", slot: "title",
            });
            rewards.title = STREAK_50_TITLE;
          } else if (filter?.level) {
            const pool = getAllGrimoireCards().filter(c => c.type === CARD_TYPES.ESCENDANT && c.level === filter.level);
            if (pool.length > 0) {
              const drop = pool[Math.floor(Math.random() * pool.length)];
              await addCardToCollection(uid, drop.id, true, RARITY.HOLO_DEPTHS, `depths:streak${t.at}`);
              rewards.streakDrops.push({ at: t.at, cardId: drop.id, cardName: drop.name });
            }
          }
        }
      }
    }
  } catch (_) { /* non-fatal — never break a recorded result */ }

  return { newElo, newPeak, eloChange: delta, rewards };
}

// ── ENDLESS DEPTHS COIN REWARD ────────────────────────────────
// Call this after recordDepthResult to award ◕ for depth wins.
// (Was DC until 2026-07-28. Depth wins are repeatable, and a repeatable
// faucet may never pay Divine Crests — Economy Law §3, the Faucet Law.
// Gatekeeper *trophies* are a separate, one-time question.)
export async function earnDepthCoins(uid, result, rankId, isGatekeeper) {
  if (result === "loss") return 0;

  let amount = 0;
  if (result === "draw") {
    amount = DEPTH_COIN_REWARDS.draw;
  } else if (result === "win") {
    amount = DEPTH_COIN_REWARDS[rankId] || DEPTH_COIN_REWARDS.escling;
    if (isGatekeeper) amount *= DEPTH_COIN_REWARDS.gatekeeperMult;
  }

  if (amount <= 0) return 0;

  // Delta-based transaction on the chocolateCoins leaf ONLY — never a
  // read-then-overwrite of the profile object (the write pattern that
  // caused the April 2026 data-loss incident).
  await runTransaction(ref(db, `profiles/${uid}/chocolateCoins`), (cur) => (cur || 0) + amount);
  return amount;
}

// @deprecated — Depths pays Chocolate Coins now.
// `earnDepthCrests` removed 2026-08-18 — the Depths pay Chocolate Coins
// and always did; only the name said otherwise. Use earnDepthCoins().

export async function recordGatekeeperDefeat(uid, gatekeeperId) {
  const snap = await get(ref(db, `profiles/${uid}/endlessDepths/defeatedGatekeepers`));
  const current = snap.exists() ? snap.val() : [];
  const updated = [...new Set([...current, gatekeeperId])];
  await update(ref(db, `profiles/${uid}/endlessDepths`), { defeatedGatekeepers: updated });
  // First gatekeeper ever felled. Idempotent; fires once.
  try { await grantAchievement(_uidKey(uid, "recordGatekeeperDefeat"), "terror:gatekeeper_first", "depths:gatekeeper"); }
  catch (_) { /* non-fatal */ }
}

// ═══════════════════════════════════════════════════════════════
// ENDLESS DEPTHS — TROPHIES, STREAKS, INNER CIRCLE
// ═══════════════════════════════════════════════════════════════
// Two layers of Depths rewards (Inner Circle removed July 2026 —
// it was never wired and added rules complexity for little payoff):
//   1. Gatekeeper Trophies — beat a gate boss, get a themed holo card
//   2. Streak Drops — consecutive wins trigger tiered card drops;
//      50-streak grants the "the Unbroken" profile title
// ═══════════════════════════════════════════════════════════════

// ── LAYER 1: GATEKEEPER TROPHIES ───────────────────────────────
// Canonical gatekeepers per Terror lore. Each gates a specific
// tier transition and drops a themed card as holo_depths.
//
// eloFloor    = minimum Elo required to challenge this gatekeeper
// tierBlocked = which rank they stand between
// trophyCardId = card that drops on first defeat (holo_depths)
export const GATEKEEPER_TROPHIES = {
  suhnker: {
    id:           "suhnker",
    name:         "S'uhnker",
    title:        "Sentinel of the Outer Gate",
    eloFloor:     400,
    tierBlocked:  "beb",
    trophyCardId: "premier_dragon_beb",
    trophyNote:   "A Dragon Beb, newly branded by the Depths.",
  },
  zhefir: {
    id:           "zhefir",
    name:         "Z'hefir",
    title:        "Keeper of Shadows",
    eloFloor:     800,
    tierBlocked:  "champion",
    trophyCardId: "spectral_tiger_lord",
    trophyNote:   "The apex predator of the Shadow Forest, bound in depth-ink.",
  },
  tyrent: {
    id:           "tyrent",
    name:         "T'yrent",
    title:        "The Last Flame",
    eloFloor:     1200,
    tierBlocked:  "lord",
    trophyCardId: "h01_lord",
    trophyNote:   "H-01 Super Nova — the Minerium's technological apex.",
  },
  vael: {
    id:           "vael",
    name:         "V'ael",
    title:        "The Inner Minerium",
    eloFloor:     1700,
    tierBlocked:  "escendant",
    trophyCardId: "xulan_lord",
    trophyNote:   "Xulan-Shadow — hatred rewarded with a depths holo.",
  },
  ruk: {
    id:           "ruk",
    name:         "Rük",
    title:        "Fracture Made Flesh",
    eloFloor:     2200,
    tierBlocked:  "aenarchist",
    trophyCardId: "chaos_dragon_lord",
    trophyNote:   "The Chaos Dragon Lord — the Depths' ultimate trophy.",
  },
};

// Get the next gatekeeper the player hasn't beaten
export const getNextGatekeeper = (defeatedIds = []) => {
  const defeated = new Set(defeatedIds);
  const ordered = ["suhnker", "zhefir", "tyrent", "vael", "ruk"];
  for (const id of ordered) {
    if (!defeated.has(id)) return GATEKEEPER_TROPHIES[id];
  }
  return null; // all beaten
};

// ── LAYER 2: STREAK DROPS ──────────────────────────────────────
// Hit a consecutive-win threshold → tiered card drops.
// Streak counter KEEPS GOING after drop (does not reset).
// Losing resets streak to 0.
// Milestones are absolute — hitting 7 means streaks 3 AND 7
// were both earned, but each fires once per Depths run.
export const STREAK_THRESHOLDS = [
  { at: 3,  pool: "beb",       note: "You're warming up."                },
  { at: 7,  pool: "champion",  note: "Something's clicking."             },
  { at: 12, pool: "lord",      note: "Now people are watching."          },
  { at: 20, pool: "lord",      note: "This is how legends begin."        },
  { at: 50, pool: "title",     note: "They said it couldn't be done."    },
];

// Pool definitions — which card tiers live in each streak pool.
// Resolved against the Escendant roster at runtime by level.
// (Recalibrated July 2026: "supreme" and "any/choice" pools removed —
//  Supremes are a board state, not collectible cards, and the picker
//  was complexity without payoff. Two layers now: gatekeepers + streaks.)
export const STREAK_POOL_FILTERS = {
  beb:      { level: 1 },            // L1 Escendants
  champion: { level: 2 },            // L2 Escendants
  lord:     { level: 3 },            // L3 Escendants
  title:    { titleOnly: true },     // Grants profile title cosmetic (no card)
};

// The 50-streak profile title
export const STREAK_50_TITLE = "the Unbroken";

// Determine Elo tier from Elo value
export const eloTier = (elo = 0) => {
  if (elo < 400)  return "escling";
  if (elo < 800)  return "beb";
  if (elo < 1200) return "champion";
  if (elo < 1700) return "lord";
  if (elo < 2200) return "escendant";
  if (elo < 2800) return "aenarchist";
  return "solrend";
};

// ═══════════════════════════════════════════════════════════════
// COMPANION SYSTEM
// ═══════════════════════════════════════════════════════════════
// Profile companion state lives at:
//   profiles/{uid}/companions/{companionId} = { unlockedAt, source }
//   profiles/{uid}/equippedCompanion        = companionId
//
// These functions operate on uid-keyed profiles (Plaza convention).
// ═══════════════════════════════════════════════════════════════

// CREST rewards for companion milestones
export const COMPANION_REWARDS = {
  TROPHY_UNLOCK: 50,
};

// ─────────────────────────────────────────────────────────────
// unlockCompanion — grant an explicit companion unlock
// Used for: story trophies, dungeon clears, depth ranks, secrets.
// Idempotent: calling twice on same companion returns
// { success: true, alreadyUnlocked: true }.
// ─────────────────────────────────────────────────────────────
export const unlockCompanion = async (uid, companionId, source = "unknown") => {
  if (!uid || !companionId) return { success: false };
  const companion = getCompanion(companionId);
  if (!companion) return { success: false, reason: "Unknown companion." };

  const profileRef = ref(db, `profiles/${uid}`);
  const snap = await get(profileRef);
  if (!snap.exists()) return { success: false };
  const profile = snap.val();

  if (profile.companions && profile.companions[companionId]) {
    return { success: true, alreadyUnlocked: true };
  }

  const unlocks = { ...(profile.companions || {}) };
  unlocks[companionId] = { unlockedAt: Date.now(), source };

  await update(profileRef, { companions: unlocks });
  return { success: true, alreadyUnlocked: false, companion };
};

// ─────────────────────────────────────────────────────────────
// equipCompanion — set the active companion
// Charges the Sol Converter cost (varies by companion tier).
// Returns { success: false, reason, cost, have } on insufficient DC.
// ─────────────────────────────────────────────────────────────
export const equipCompanion = async (uid, companionId) => {
  if (!uid || !companionId) return { success: false };

  const profileRef = ref(db, `profiles/${uid}`);
  const snap = await get(profileRef);
  if (!snap.exists()) return { success: false };
  const profile = snap.val();

  if (profile.equippedCompanion === companionId) {
    return { success: true, alreadyEquipped: true, cost: 0 };
  }

  if (!isCompanionUnlocked(profile, companionId)) {
    return { success: false, reason: "Companion is locked." };
  }

  const cost = getSolConverterCost(companionId);
  const have = profile.chocolateCoins || 0;
  if (have < cost) {
    return { success: false, reason: "Not enough Chocolate Coins.", cost, have };
  }

  // Atomic debit first — spendCoins aborts on its own if the balance moved
  // between the read above and now. Only equip once the coins are gone.
  const paid = await spendCoins(uid, cost);
  if (!paid) return { success: false, reason: "Not enough Chocolate Coins.", cost, have };

  await update(profileRef, {
    equippedCompanion: companionId,
    [`coinHistory/${Date.now()}`]: { amount: -cost, reason: `Sol Convert: ${companionId}` },
  });

  return { success: true, cost, newBalance: have - cost };
};

// ─────────────────────────────────────────────────────────────
// 🐾 VENUS' PET SHOP — buy any companion (caP'n, 2026-09-16: "all
// companions purchasable"). The bought copy is its OWN record,
// profiles/{uid}/companionShop/{id}, with its own mark; a story or
// starter copy is never touched. Once per account per companion.
// CLAIM → PAY → SEAL, the King's Grace order: the slot is claimed
// (pending) before any ◕ moves, a refused payment releases our claim,
// and a stale pending (paid, then the tab died) reads as owned.
// Buying never equips — that is still the Sol Converter's job.
// ─────────────────────────────────────────────────────────────
export const buyCompanion = async (uid, companionId) => {
  uid = _uidKey(uid, "buyCompanion");
  if (!uid) return { success: false, reason: "Sign in first." };
  const comp = getCompanion(companionId);
  if (!comp) return { success: false, reason: "Unknown companion." };
  const cost = getCompanionShopPrice(companionId);
  if (!Number.isFinite(cost) || cost <= 0) return { success: false, reason: "Venus hasn't priced that one yet." };

  const slot = ref(db, `profiles/${uid}/companionShop/${companionId}`);
  const at = Date.now();
  let mine = false;
  const claim = await runTransaction(slot, (cur) => {
    mine = false;
    if (cur) return undefined;
    mine = true;
    return { at, paid: cost, currency: "CC", status: "pending" };
  });
  if (!claim.committed || !mine) {
    const v = claim.snapshot && claim.snapshot.val();
    if (v && v.status === "pending" && Date.now() - (v.at || 0) < COMPANION_SHOP_PENDING_MS) {
      return { success: false, reason: "That purchase is already going through." };
    }
    return { success: true, alreadyOwned: true };
  }

  let paid = false;
  try { paid = await spendCoins(uid, cost); } catch (_) { paid = false; }
  if (!paid) {
    await runTransaction(slot, (cur) => (cur && cur.at === at && cur.status === "pending" ? null : undefined));
    return { success: false, reason: "Not enough Chocolate Coins.", cost };
  }
  await runTransaction(slot, (cur) => {
    if (!cur || cur.at !== at) return undefined;
    const { status, ...rest } = cur;
    return rest;
  });
  await update(ref(db, `profiles/${uid}`), {
    [`coinHistory/${at}_${Math.floor(Math.random() * 1e9).toString(36)}`]: { amount: -cost, reason: `Venus' pet shop: ${companionId}` },
  });
  return { success: true, cost };
};

// ─────────────────────────────────────────────────────────────
// ♛ KING'S GRACE — Terror's wrapper around kingsGrace.equipGrace.
// The kit owns the write; Terror adds its two achievements, which were
// always about wearing a sheen, and still are.
// ─────────────────────────────────────────────────────────────
export const equipKingsGrace = async (uid, companionId, graceId, source = null) => {
  const r = await kitEquipGrace(uid, companionId, graceId, source);
  if (!r.success || !graceId) return r;
  try {
    const u = _uidKey(uid, "equipKingsGrace");
    await grantAchievement(u, "terror:radiant_first", "grace:equip");
    if ((r.distinctCompanions || 0) >= 3) await grantAchievement(u, "terror:three_radiant", "grace:trinity");
  } catch (_) { /* non-fatal */ }
  return r;
};

// ─────────────────────────────────────────────────────────────
// ◆ RADIANT VERSIONS ◆   ⚰ RETIRED 2026-09-16 — NO CALLER.
// Superseded by King's Grace (kingsGrace.js + the Vault's Grace shelf).
// Kept so `git log` is not the only place the old shop is readable, and
// because `unlockedRadiants` is still READ as owned. ⚠ equipRadiant
// below swapped your companion for free (a Sol Converter bypass) —
// that is why the new equip never touches equippedCompanion.
// buyRadiant   — purchase a (companion, tier) foil sheen. Append-only:
//                writes a single unlockedRadiants key, never overwrites.
// equipRadiant — set the active radiant sheen (or clear with null).
// ─────────────────────────────────────────────────────────────
export const buyRadiant = async (uid, companionId, tier) => {
  if (!uid || !companionId || !tier) return { success: false, reason: "Invalid radiant." };

  const profileRef = ref(db, `profiles/${uid}`);
  const snap = await get(profileRef);
  if (!snap.exists()) return { success: false };
  const profile = snap.val();

  // Must own the companion to dress it.
  if (!isCompanionUnlocked(profile, companionId)) {
    return { success: false, reason: "You don't own that companion yet." };
  }

  const key = radiantKey(companionId, tier);
  if (profile.unlockedRadiants && profile.unlockedRadiants[key]) {
    return { success: true, alreadyOwned: true };
  }

  const cost = getRadiantCost(tier);
  const have = profile.chocolateCoins || 0;
  if (have < cost) return { success: false, reason: "Not enough Chocolate Coins.", cost, have };

  const paid = await spendCoins(uid, cost);
  if (!paid) return { success: false, reason: "Not enough Chocolate Coins.", cost, have };

  await update(profileRef, {
    [`unlockedRadiants/${key}`]: { at: Date.now(), companionId, tier },
    [`coinHistory/${Date.now()}`]: { amount: -cost, reason: `Radiant: ${companionId} ${tier}` },
  });

  return { success: true, cost, newBalance: have - cost };
};

// Set (or clear) the radiant sheen worn by the equipped companion.
// Pass tier=null to remove. Validates ownership of the pairing.
export const equipRadiant = async (uid, companionId, tier) => {
  if (!uid) return { success: false };
  const profileRef = ref(db, `profiles/${uid}`);
  const snap = await get(profileRef);
  if (!snap.exists()) return { success: false };
  const profile = snap.val();

  if (tier === null) {
    await update(profileRef, { equippedCompanionRadiant: null });
    return { success: true, cleared: true };
  }

  const key = radiantKey(companionId, tier);
  if (!(profile.unlockedRadiants && profile.unlockedRadiants[key])) {
    return { success: false, reason: "Radiant not owned." };
  }

  await update(profileRef, {
    equippedCompanion: companionId,
    equippedCompanionRadiant: tier,
  });

  // ── Radiant achievements ──────────────────────────────────────
  // radiant_first: equipping any radiant sheen. three_radiant: having
  // unlocked radiants across 3 DISTINCT companions (Radiant Trinity).
  try {
    const u = _uidKey(uid, "equipRadiant");
    await grantAchievement(u, "terror:radiant_first", "radiant:equip");
    const owned = profile.unlockedRadiants || {};
    const distinctCompanions = new Set(
      Object.values(owned).map((r) => r && r.companionId).filter(Boolean)
    );
    if (distinctCompanions.size >= 3) {
      await grantAchievement(u, "terror:three_radiant", "radiant:trinity");
    }
  } catch (_) { /* non-fatal */ }

  return { success: true, tier };
};

// ─────────────────────────────────────────────────────────────
// ensureStarterCompanion — one-time migration helper
// On login, if the player has a starterDeckId but no equipped
// companion, auto-equip their starter Beb for free and record
// the unlock. Safe to call on every login (idempotent).
// ─────────────────────────────────────────────────────────────
export const ensureStarterCompanion = async (uid) => {
  if (!uid) return { success: false };
  const profileRef = ref(db, `profiles/${uid}`);
  const snap = await get(profileRef);
  if (!snap.exists()) return { success: false };
  const profile = snap.val();

  if (profile.equippedCompanion) return { success: true, noChange: true };
  if (!profile.starterDeckId)    return { success: true, noChange: true };

  const starterCompanionId = getStarterCompanionId(profile.starterDeckId);
  if (!starterCompanionId) return { success: false };

  await update(profileRef, {
    equippedCompanion: starterCompanionId,
    [`companions/${starterCompanionId}`]: { unlockedAt: Date.now(), source: "starter" },
  });

  return { success: true, companionId: starterCompanionId };
};

// ─────────────────────────────────────────────────────────────
// backfillStoryTrophies — retroactive trophy unlock
// Walks profile.storyProgress.defeatedOpponents and grants any
// trophy companions the player earned before the companion
// system existed. Idempotent; safe to call on every login.
// ─────────────────────────────────────────────────────────────
// ⭐ THE STORY TROPHY TABLE — ONE COPY, TWO READERS.
// Defeating one of these grants its trophy companion. The keys are
// `node.boss` ids off the world map (worldData RAW_NODES) and the
// legacy `storyProgress.defeatedOpponents` ids, which is why both
// spellings are present: the map says "gk_suhnker", the old Dungeon-1
// recorder said "suhnker", and both mean the same fight.
//
// Exported because worldData.recordNodeClear now grants these LIVE on
// first clear — until 2026-08-20 unlockCompanion had no callers at all
// and all four of these, including the game's only mythic, were
// unobtainable by any route.
export const STORY_TROPHY_BY_BOSS = {
  suhnker: "suhnker_trophy",  gk_suhnker: "suhnker_trophy",
  zhefir:  "zhefir_trophy",   gk_zhefir:  "zhefir_trophy",
  tyrent:  "tyrent_trophy",   gk_tyrent:  "tyrent_trophy",
  jynxi:   "jynxi",
};

export const backfillStoryTrophies = async (uid) => {
  if (!uid) return;
  const profileRef = ref(db, `profiles/${uid}`);
  const snap = await get(profileRef);
  if (!snap.exists()) return;
  const profile = snap.val();

  const defeated = profile.storyProgress?.defeatedOpponents || [];
  const current  = profile.companions || {};
  const updates  = {};
  let added = 0;

  // Story trophy mapping — see STORY_TROPHY_BY_BOSS above. Read, not
  // retyped: the world map grants these live now and the two paths must
  // never be able to disagree about which fight owns which companion.
  const trophyMap = STORY_TROPHY_BY_BOSS;

  for (const opponentId of defeated) {
    const trophyId = trophyMap[opponentId];
    if (!trophyId) continue;
    if (current[trophyId]) continue;
    updates[`companions/${trophyId}`] = { unlockedAt: Date.now(), source: `backfill:${opponentId}` };
    added++;
  }

  if (added > 0) await update(profileRef, updates);
  return { success: true, added };
};

// ════════════════════════════════════════════════════════════════════
// IE CURRENCY — TERROR READ-ONLY SLICE
// ════════════════════════════════════════════════════════════════════
//
// ⚠ EXCHANGES DO NOT HAPPEN IN TERROR ⚠
//
// Per IE team alignment (Apr 2026): currency exchange is Pierre's
// exclusive responsibility. The Crest Well at W'ily's Pier is the
// ONLY place DC ↔ SE conversion happens in the Illegal Empire.
//
// If you're about to add an exchange modal to Terror — stop. Send
// the player to unstable-pierre.pages.dev via the blue portal.
//
// Terror is allowed to READ any currency balance for display
// (e.g., showing SE Coins in the header HUD), but must NEVER write
// to snakeEyesCoins, exchangeHistory, or lastExchangeAt. Those
// paths belong to Pierre + Snake Eyes.
//
// This slim block provides only the read helpers + display utilities
// Terror needs. The full exchange API (performExchange, previewExchange,
// cooldowns, rates, grants) lives in Pierre's and Snake Eyes' copies
// of progression.js — deliberately NOT here.
//
// ◕ AMENDED 2026-07-28 — THE CURRENCY RESTRUCTURE
//
// Terror's own money is CHOCOLATE COINS (`chocolateCoins`), written via
// awardCoins/spendCoins earlier in this file. That is Terror's one lane.
//
// DIVINE CRESTS are no longer Terror's money. They are the Empire-wide
// RESERVE, owned by no title. Any registered title may GRANT them —
// that is DC's single shared exception to the lane law — and every title
// does it through the ONE helper below, which nobody reimplements.
//
// The Faucet Law: DC comes from one-time sources or the Crest Well only.
// If a source can pay twice, it pays Chocolate Coins. No exceptions.
//
// DC ships in this pass as PROOF OF CONCEPT: visible, grantable, and
// deliberately NOT spendable in Terror yet. Mythicals move behind DC in
// a later pass once the plumbing is proven.
// ════════════════════════════════════════════════════════════════════

// ── CURRENCY REGISTRY (for display labels / icons) ────────────────────
// Terror uses this to render "◆ DC" and "◈ SE" labels in UI.
// Keep shape identical to Pierre's / Snake Eyes' copies — this is the
// canonical IE currency registry.
export const CURRENCIES = {
  DC: {
    id:    "DC",
    name:  "Divine Crests",
    short: "DC",
    path:  "divineCrests",
    icon:  "◆",
  },
  CC: {
    id:    "CC",
    name:  "Chocolate Coins",
    short: "CC",
    path:  "chocolateCoins",
    icon:  "◕",
  },
  SE: {
    id:    "SE",
    name:  "Snake Eyes Coins",
    short: "SE",
    path:  "snakeEyesCoins",
    icon:  "◈",
  },
};

// ── ◆ THE SHARED DC GRANT — one implementation, every title ───────────
// The ONE way any IE title puts Divine Crests into a profile. Delta
// transaction on the leaf, append-only audit row, never a full-object
// write. Copy this block VERBATIM into Pierre's and Snake Eyes' copies —
// same shape everywhere, exactly like the cosmetics block below.
//
// ⚠ THE FAUCET LAW — DC only ever comes from a source a player cannot
// repeat: a boss felled once, a secret found once, an achievement, or the
// Crest Well. `reason` is mandatory and lands in the audit row, which is
// how a repeatable faucet gets caught in review. If you cannot name a
// one-time reason, you are looking at a Chocolate Coins payout.
//
// See spendDivineCrests below for the debit — THE ENDGAME RULING made the
// reserve spendable and this is the faucet that finally fills it.
//
// ═══════════════════════════════════════════════════════════════
// ⭐ THIS IS TERROR'S ONLY ◆ FAUCET. 2026-08-20.
// ═══════════════════════════════════════════════════════════════
// Until today it had ZERO callers, and so did ieEconomy's grantCrests,
// and so did deltaCurrency, and so did both read helpers. The only ◆ a
// player would ever hold was the 300 in the pre-alpha purse — which
// comes off on THE LAST GATE. So at launch numbers the entire Foil
// Vault and the mythic coin skin were UNBUYABLE: two sinks, zero
// faucets. caP'n's ruling, 2026-08-20, on where they come from:
//
//   *"it will be from easter eggs, achievements, and challenges, maybe
//    a couple other ways, fountain, V'ael randomly etc."*
//
// All four of those now call THIS function and no other:
//   · achievementWrites.grantAchievement   → crestsFor(id), 1/3/5 by rank
//   · worldData.recordNodeClear            → the easter eggs
//   · FactionHall's challenge claim        → the three faction ladders
//   · fountain.tossCoin                    → the coin in the water
//
// ⚠ TWO grantCrests EXISTED AND THEY DID NOT AGREE. ieEconomy's is the
// cross-game kit's — byte-identical in every IE repo, chunked against
// GRANT_CEILING so a big grant cannot trip the Firebase clamp, and it
// mints provenance to games/pierre/mint. Terror's own wrote the whole
// amount in ONE transaction (so the day anything granted more than the
// ceiling it would have failed the rules and silently paid nothing) and
// logged to a different ledger. Two writers, one balance, two audit
// trails that could disagree.
//
// ⭐ SO THIS IS A WRAPPER NOW, NOT A SECOND IMPLEMENTATION. The balance
// move and the Empire's mint row are the kit's job and happen exactly
// once, in exactly one place, in every title. What stays here is the
// two things that are Terror's:
//
//   1 · THE `reason` REFUSAL. The kit defaults its sourceKey to
//       "unknown" and mints anyway. This refuses. A faucet you cannot
//       name in an audit row is a faucet nobody will catch repeating,
//       and that refusal is the only reason this function still exists.
//   2 · THE LOCAL RECEIPT at profiles/{uid}/crestHistory, which is what
//       a Terror screen reads when it wants to show a player where
//       their own Crests came from. The mint log is the Empire's copy;
//       this is the player's.
//
// Do not delete the kit's and do not edit it from inside a title — it
// is vendored, and five versions of achievements.js is what happens.
export const grantCrests = async (uid, amount, reason) => {
  uid = _uidKey(uid, "grantCrests");
  if (!uid || !amount || amount <= 0) return;
  if (!reason) {
    console.warn("[grantCrests] refused: every DC grant needs a one-time `reason`.");
    return;
  }
  const paid = await ieGrantCrests(uid, amount, reason);
  if (!paid) return;
  // Append-only local receipt. `paid` and not `amount`, so it can never
  // claim more than the lane actually moved.
  await update(ref(db, `profiles/${uid}`), {
    [`crestHistory/${Date.now()}`]: { amount: paid, reason },
  });
  return paid;
};

// ═══════════════════════════════════════════════════════════════
// ◈ THE SHELVES — storyVariants · easterEggVariants · grimoirePages
// ═══════════════════════════════════════════════════════════════
// Three lists read by the Codex's Story tab and the Plaza's Grand
// Archive, and — until 2026-08-20 — written by nothing, anywhere. Both
// screens rendered a labelled empty state promising the player things
// that were findable. Shipping an empty shelf labelled as findable is
// worse than not shipping the shelf.
//
// ⚠ THEY ARE ARRAYS BECAUSE BOTH READERS TREAT THEM AS ARRAYS —
// `.length` for the tab counts, `.map` for the tiles. RTDB reads a
// node whose keys are 0..n back as a JS array, so an append that keeps
// the keys dense keeps both readers working with no migration and no
// screen edit. The whole node is transacted rather than pushed to,
// which is the price of that: it is a handful of small objects and the
// alternative was rewriting two screens to consume a map.
//
// Idempotent on `id`. Every caller here is a first-clear or a
// first-secret path that is ALREADY transaction-guarded upstream, so
// this is the second lock and not the first.
export const appendShelfItem = async (uid, shelf, entry) => {
  uid = _uidKey(uid, "appendShelfItem");
  if (!uid || !shelf || !entry?.id) return false;
  const r = ref(db, `profiles/${uid}/${shelf}`);
  const res = await runTransaction(r, (cur) => {
    const list = Array.isArray(cur) ? cur.filter(Boolean) : (cur ? Object.values(cur).filter(Boolean) : []);
    if (list.some((x) => x && x.id === entry.id)) return undefined;   // abort — already shelved
    return [...list, entry];
  });
  return !!res.committed;
};

// ═══════════════════════════════════════════════════════════════
// ◆ A NAMED RUNG OF THE INNER EMPERIUM, FELLED
// ═══════════════════════════════════════════════════════════════
// The ladder's other door. recordDepthResult counts a TOTEM bout;
// this counts a duel against one of the named villains — and until
// 2026-08-20 nothing counted that at all.
//
// ⚠ `isInnerEmperium` had been riding in the launch meta for months
// and was READ IN EXACTLY ONE PLACE: App's free-play gate, which
// EXCLUDES it, on the grounds that "those three paths record through
// their own branches above." Two of the three did. This one had no
// branch. So an Inner Emperium victory recorded nothing — no coins,
// no totalWins, no leaderboard, no achievement — and, once the ladder
// was restored, no `innerWins` either, which left every rung above
// the first unreachable by playing the ladder itself.
//
// Two writes, in the order they must happen:
//
//   1 · THE ORDINARY WIN, through recordFreePlayWin — the one place
//       that already grants NPC_WIN coins, bumps totalWins, mirrors
//       the leaderboard and hangs the H5 faction-war tally off it.
//       Repeatable, coins only, exactly like every other rematch.
//
//   2 · THE FIRST KILL. `innerWins` bumps once per VILLAIN, ever,
//       behind a transaction-guarded stamp — not once per win. A
//       counter that gates permanent unlocks must not be farmable by
//       re-beating the easiest rung, and the villain's authored
//       `rewardCrests` rides the same stamp because a first-kill is a
//       legal ◆ faucet under the Faucet Law and a rematch is not.
export const recordInnerWin = async (uid, villainId) => {
  uid = _uidKey(uid, "recordInnerWin");
  if (!uid || !villainId) return null;

  // ── THE ORDINARY HALF ────────────────────────────────────────
  // ⚠ NOT THROUGH recordFreePlayWin, THOUGH THE FIRST CUT OF THIS DID.
  // That function does the four things wanted here — NPC_WIN coins,
  // totalWins, the leaderboard mirror, first-win bonus — and TWO that
  // are not: it bumps `freePlayWins` and fires `terror:first_freeplay`
  // and `terror:freeplay_50`. Routing the ladder through it would have
  // made "The Free Spirit" partly earnable by grinding the Inner
  // Emperium, and a player whose very first duel was an Inner one would
  // have been handed a free-play achievement for a fight that is the
  // opposite of free play. An achievement that can be earned by not
  // doing the thing it names is a broken achievement.
  //
  // So the four wanted things are done here, in the open, and the two
  // unwanted ones are simply not. Fire-and-forget by design — a
  // bookkeeping failure must never cost the player the duel they won.
  try {
    const pRef = ref(db, `profiles/${uid}`);
    const snap = await get(pRef);
    const prof = snap.exists() ? (snap.val() || {}) : {};
    const nextWins = (prof.totalWins || 0) + 1;
    await update(pRef, { totalWins: nextWins });
    await mirrorLeaderboard(uid, prof.name, nextWins);
    await awardCoins(uid, COIN_REWARDS.NPC_WIN, "innerEmperium:win");
    // FIRST BLOOD is mode-agnostic by its own banner — "your very first
    // match", not "your very first free-play match" — so it belongs.
    await grantFirstBlood(uid, "innerEmperium:win");
  } catch (e) { console.warn("[innerWin] win record failed:", e && e.message); }

  const stampRef = ref(db, `profiles/${uid}/innerFelled/${villainId}`);
  const res = await runTransaction(stampRef, (cur) => (cur ? undefined : Date.now()));
  if (!res.committed) return { firstKill: false };

  try {
    await runTransaction(ref(db, `profiles/${uid}/innerWins`), (cur) => (cur || 0) + 1);
  } catch (e) { console.warn("[innerWin] counter bump failed:", e && e.message); }

  const villain = INNER_VILLAINS.find(v => v.id === villainId);
  const owed = Number(villain?.rewardCrests) || 0;
  if (owed > 0) {
    // ⭐ IN COINS, NOT CRESTS, and the field name is a fossil. Every
    // other `rewardCrests` in storyData is spent through awardCoins —
    // Terror's everyday money is Chocolate Coins and the field was
    // named before Divine Crests existed as a separate reserve. Paying
    // 500 ◆ for beating `the_deep` would hand a single player more of
    // the Empire-wide reserve than the entire Story Mode pays out.
    try { await awardCoins(uid, owed, `innerEmperium:${villainId}`); }
    catch (e) { console.warn("[innerWin] coin pour failed:", e && e.message); }
  }
  return { firstKill: true, coins: owed };
};

// ── BALANCE READERS ───────────────────────────────────────────────────
// Read any IE currency balance for a profile. Terror writes CC via
// awardCoins/spendCoins and grants DC via grantCrests; SE is Snake Eyes'
// to write. Everything else here is read-only.

// ── ◆ SPEND THE RESERVE — THE ENDGAME RULING, 2026-08-18 ─────────────
// FORWARDED, not reimplemented. `ieEconomy.js` is the canonical money
// file, byte-identical in every IE repo, and it already carries the
// correct atomic leaf debit. A sixth copy of eight lines is how the
// Empire ended up with five versions of achievements.js — so this is a
// re-export under a name that cannot be confused with anything.
//
// The name is deliberately NOT `spendCrests`: that string now means one
// specific thing (ieEconomy's) and Terror having a second function
// answering to it is exactly the landmine that was just removed.
//
// Returns true only on a committed debit. Aborts untouched if short —
// no read-then-write, so two tabs cannot spend the same Crest twice.
export const spendDivineCrests = async (uid, amount) => {
  uid = _uidKey(uid, "spendDivineCrests");
  if (!uid) return false;
  return ieSpendCrests(uid, amount);
};

// The reserve balance, for a shelf that needs to grey a button out.
export const getCrestBalance = async (uid) => {
  uid = _uidKey(uid, "getCrestBalance");
  if (!uid) return 0;
  const snap = await get(ref(db, `profiles/${uid}/divineCrests`));
  return snap.val() || 0;
};

export const listenToCrestBalance = (uid, cb) => {
  if (!uid || typeof cb !== "function") return () => {};
  return onValue(ref(db, `profiles/${uid}/divineCrests`), (s) => cb(s.val() || 0), () => cb(0));
};

export const getBalance = async (uid, currencyId) => {
  const curr = CURRENCIES[currencyId];
  if (!curr) throw new Error(`Unknown currency: ${currencyId}`);
  const snap = await get(ref(db, `profiles/${uid}/${curr.path}`));
  return snap.val() || 0;
};

export const listenToBalance = (uid, currencyId, callback) => {
  const curr = CURRENCIES[currencyId];
  if (!curr) throw new Error(`Unknown currency: ${currencyId}`);
  return onValue(
    ref(db, `profiles/${uid}/${curr.path}`),
    (snap) => callback(snap.val() || 0)
  );
};

// ── DISPLAY FORMATTERS ────────────────────────────────────────────────
// Pure utilities — shared shape with Pierre / Snake Eyes so the same
// "◆ 1.2k" renders identically across every IE app.

export const formatCurrency = (amount, currencyId = null) => {
  const n = Math.max(0, Math.floor(amount || 0));
  let formatted;
  if (n >= 1_000_000)  formatted = (n / 1_000_000).toFixed(1).replace(/\.0$/, "") + "M";
  else if (n >= 1_000) formatted = (n / 1_000).toFixed(1).replace(/\.0$/, "") + "k";
  else                 formatted = String(n);

  if (!currencyId) return formatted;
  const curr = CURRENCIES[currencyId];
  return curr ? `${curr.icon} ${formatted}` : formatted;
};

// ════════════════════════════════════════════════════════════════════
// END OF IE CURRENCY READ-ONLY SLICE
// ════════════════════════════════════════════════════════════════════


// ════════════════════════════════════════════════════════════════════
// IE COSMETICS — SHARED IDENTITY SLICE
// ════════════════════════════════════════════════════════════════════
//
// ⚠ EQUIP-ANYWHERE LANE RULE ⚠
//
// Per IE Cosmetic Schema v1.1 (Apr 2026): cosmetics are the INVERSE of
// currency. Any IE game can grant unlocks. Any IE game can equip. All
// IE games render the equipped state immediately via listenToEquipped.
//
// The Pier is the DISCOVERY/ORGANIZATION hub (the Wardrobe) — it is
// NOT an equipping chokepoint. You equip wherever you earned it.
//
// This block is safe to paste VERBATIM into:
//   - Terror's   progression.js
//   - Pierre's   progression.js
//   - Snake Eyes' progression.js
//
// Shape MUST stay identical across all three copies. If you change
// this file, update IE_COSMETIC_SCHEMA_v1.md first, bump the version,
// then propagate to all three apps in the same session.
//
// Canonical reference: IE_COSMETIC_SCHEMA_v1.md
// ════════════════════════════════════════════════════════════════════

// ── COSMETIC SLOTS ────────────────────────────────────────────────────
// Six fixed slots, one item per slot. Locked by schema v1.1.
// If you need a "particle trail + glow halo + aura" combo, those are
// sub-effects of the `aura` slot — do NOT add new top-level slots.

export const COSMETIC_SLOTS = ["head", "face", "body", "back", "aura", "title"];

// ── RARITY TIERS ──────────────────────────────────────────────────────
// Six tiers. Rarity is metadata — each game interprets it with its own
// aesthetic (Terror: manuscript-ink legendary; Casino: neon holo; etc.)

export const COSMETIC_RARITIES = [
  "common", "uncommon", "rare", "epic", "legendary", "mythic",
];

// Display metadata — shared so "Epic" and "Mythic" feel consistent
// across every IE app even if their visual treatment differs.
export const RARITY_META = {
  common:    { order: 0, label: "Common",    short: "C",  tint: "#9aa4ad" },
  uncommon:  { order: 1, label: "Uncommon",  short: "U",  tint: "#4da36a" },
  rare:      { order: 2, label: "Rare",      short: "R",  tint: "#4a88c8" },
  epic:      { order: 3, label: "Epic",      short: "E",  tint: "#8a5cd6" },
  legendary: { order: 4, label: "Legendary", short: "L",  tint: "#d4a84b" },
  mythic:    { order: 5, label: "Mythic",    short: "M",  tint: "#c94a6b" },
};

// ── VALIDATION HELPERS (internal) ─────────────────────────────────────

const _validSlot   = (slot)   => COSMETIC_SLOTS.includes(slot);
const _validRarity = (rarity) => COSMETIC_RARITIES.includes(rarity);

// ── GRANT ─────────────────────────────────────────────────────────────
// Award a cosmetic unlock. Append-only: if already unlocked, this is a
// no-op — original unlockedAt and source are preserved.
//
// meta: { source: "terror:...", rarity: "legendary", slot: "head" }
//   - source: namespaced string identifying where/why it was earned
//   - rarity: one of COSMETIC_RARITIES
//   - slot:   one of COSMETIC_SLOTS (the slot this cosmetic occupies)
//
// Returns: { success, alreadyUnlocked, reason? }

export const grantCosmetic = async (uid, cosmeticId, meta = {}) => {
  if (!uid || !cosmeticId) {
    return { success: false, reason: "Missing uid or cosmeticId." };
  }
  const { source, rarity, slot } = meta;
  if (!_validSlot(slot)) {
    return { success: false, reason: `Invalid slot: ${slot}` };
  }
  if (!_validRarity(rarity)) {
    return { success: false, reason: `Invalid rarity: ${rarity}` };
  }
  if (!source || typeof source !== "string") {
    return { success: false, reason: "Missing or invalid source." };
  }

  const cosmeticRef = ref(db, `profiles/${uid}/unlockedCosmetics/${cosmeticId}`);
  const snap = await get(cosmeticRef);

  if (snap.exists()) {
    // Append-only: never overwrite. Provenance is permanent.
    return { success: true, alreadyUnlocked: true };
  }

  await set(cosmeticRef, {
    unlockedAt: Date.now(),
    source,
    rarity,
    slot,
  });

  return { success: true, alreadyUnlocked: false };
};

// ── EQUIP / UNEQUIP ───────────────────────────────────────────────────
// Equip a cosmetic to its slot. Verifies the player owns it first, and
// that the stored slot matches the target slot (prevents ID collisions
// from cross-app catalogs silently equipping to the wrong slot).
//
// Returns: { success, reason? }

export const equipCosmetic = async (uid, slot, cosmeticId) => {
  if (!uid || !slot || !cosmeticId) {
    return { success: false, reason: "Missing uid, slot, or cosmeticId." };
  }
  if (!_validSlot(slot)) {
    return { success: false, reason: `Invalid slot: ${slot}` };
  }

  const unlockSnap = await get(ref(db, `profiles/${uid}/unlockedCosmetics/${cosmeticId}`));
  if (!unlockSnap.exists()) {
    return { success: false, reason: "Not unlocked." };
  }

  const unlock = unlockSnap.val();
  if (unlock.slot !== slot) {
    return {
      success: false,
      reason: `Slot mismatch: ${cosmeticId} belongs in "${unlock.slot}", not "${slot}".`,
    };
  }

  // Check if this is the first cosmetic ever equipped
  const equippedRef = ref(db, `profiles/${uid}/equipped`);
  const equippedSnap = await get(equippedRef);
  const currentEquipped = equippedSnap.val() || {};
  const hasAnyEquipped = Object.values(currentEquipped).some(v => v !== null);

  await update(equippedRef, { [slot]: cosmeticId });
  
  // First cosmetic achievement
  if (!hasAnyEquipped) {
    await grantAchievement(uid, "terror:cosmetic_first", "wardrobe:equip");
  }

  return { success: true };
};

// Unequip a slot (equip null).
export const unequipSlot = async (uid, slot) => {
  if (!uid || !_validSlot(slot)) {
    return { success: false, reason: `Invalid slot: ${slot}` };
  }
  await update(ref(db, `profiles/${uid}/equipped`), { [slot]: null });
  return { success: true };
};

// Unequip every slot at once (useful for "reset to default" UI).
export const unequipAll = async (uid) => {
  if (!uid) return { success: false };
  const cleared = {};
  for (const s of COSMETIC_SLOTS) cleared[s] = null;
  await update(ref(db, `profiles/${uid}/equipped`), cleared);
  return { success: true };
};

// ── READ HELPERS ──────────────────────────────────────────────────────

// One-shot read of all equipped slots. Returns a plain object with all
// six slots populated (values are cosmeticId strings or null).
export const getEquipped = async (uid) => {
  const empty = Object.fromEntries(COSMETIC_SLOTS.map((s) => [s, null]));
  if (!uid) return empty;
  const snap = await get(ref(db, `profiles/${uid}/equipped`));
  const val = snap.val() || {};
  return { ...empty, ...val };
};

// Live listener for equipped state. Callback fires with the normalized
// { head, face, body, back, aura, title } object on every change.
// Returns the unsubscribe function.
export const listenToEquipped = (uid, callback) => {
  const empty = Object.fromEntries(COSMETIC_SLOTS.map((s) => [s, null]));
  return onValue(ref(db, `profiles/${uid}/equipped`), (snap) => {
    callback({ ...empty, ...(snap.val() || {}) });
  });
};

// One-shot read of the full unlock map.
// Shape: { [cosmeticId]: { unlockedAt, source, rarity, slot } }
export const getUnlocked = async (uid) => {
  if (!uid) return {};
  const snap = await get(ref(db, `profiles/${uid}/unlockedCosmetics`));
  return snap.val() || {};
};

// Live listener for the full unlock map.
export const listenToUnlocked = (uid, callback) => {
  return onValue(ref(db, `profiles/${uid}/unlockedCosmetics`), (snap) => {
    callback(snap.val() || {});
  });
};

// Quick check: does this uid own this cosmetic?
export const isUnlocked = async (uid, cosmeticId) => {
  if (!uid || !cosmeticId) return false;
  const snap = await get(ref(db, `profiles/${uid}/unlockedCosmetics/${cosmeticId}`));
  return snap.exists();
};

// ── SOURCE/PROVENANCE QUERIES ─────────────────────────────────────────
// These operate on an already-fetched unlock map so callers don't pay
// for redundant Firebase reads when they already have the data.

// Filter unlocks to those earned in a given app namespace.
// appPrefix: "terror" | "casino" | "pier" | "empire" | ...
export const filterUnlocksByApp = (unlockMap, appPrefix) => {
  const out = {};
  const prefix = `${appPrefix}:`;
  for (const [id, entry] of Object.entries(unlockMap || {})) {
    if (typeof entry?.source === "string" && entry.source.startsWith(prefix)) {
      out[id] = entry;
    }
  }
  return out;
};

// Filter unlocks to those matching a slot.
export const filterUnlocksBySlot = (unlockMap, slot) => {
  const out = {};
  for (const [id, entry] of Object.entries(unlockMap || {})) {
    if (entry?.slot === slot) out[id] = entry;
  }
  return out;
};

// Filter unlocks to those matching a rarity.
export const filterUnlocksByRarity = (unlockMap, rarity) => {
  const out = {};
  for (const [id, entry] of Object.entries(unlockMap || {})) {
    if (entry?.rarity === rarity) out[id] = entry;
  }
  return out;
};

// Sort unlocks most-recent-first. Returns an array of [id, entry] tuples.
export const sortUnlocksByRecency = (unlockMap) => {
  return Object.entries(unlockMap || {})
    .sort((a, b) => (b[1]?.unlockedAt || 0) - (a[1]?.unlockedAt || 0));
};

// Sort unlocks by rarity (rarest first). Returns [id, entry] tuples.
export const sortUnlocksByRarity = (unlockMap) => {
  return Object.entries(unlockMap || {}).sort((a, b) => {
    const ra = RARITY_META[a[1]?.rarity]?.order ?? -1;
    const rb = RARITY_META[b[1]?.rarity]?.order ?? -1;
    return rb - ra;
  });
};

// ════════════════════════════════════════════════════════════════════
// END OF IE COSMETICS — SHARED IDENTITY SLICE
// ════════════════════════════════════════════════════════════════════

// ─────────────────────────────────────────────────────────────
// COMPANION PETTING — Session-Scoped Achievement Tracking
// ─────────────────────────────────────────────────────────────
// Tracks cumulative pet counts for milestones. Called from
// CompanionInteraction.onPet callback during each petting session.
// Fires achievements at 100, 500 pet milestones (idempotent).

export const recordCompanionPet = async (uid, sessionPets) => {
  // sessionPets is the tap count this session (from CompanionInteraction state)
  // We increment the profile's total and check milestones.
  if (!uid || !sessionPets) return;

  const profileRef = ref(db, `profiles/${uid}`);
  const snap = await get(profileRef);
  if (!snap.exists()) return;
  
  const profile = snap.val();
  const currentTotal = (profile.totalPets || 0) + sessionPets;
  
  // Write cumulative count
  await update(profileRef, { totalPets: currentTotal });

  // Check achievement milestones (idempotent due to grantAchievement)
  if (currentTotal >= 100) {
    await grantAchievement(uid, "terror:pet_100", "companion:pet");
  }
  if (currentTotal >= 500) {
    await grantAchievement(uid, "terror:pet_500", "companion:pet");
  }
};


// ─────────────────────────────────────────────────────────────
// ROOM BACKDROPS — the Backdrop Atelier economy (July 8, 2026)
// ─────────────────────────────────────────────────────────────
// Schema (NEW node — add to the hardening-session whitelist):
//   profiles/{uid}/roomBackdrops/
//     equipped : string (backdrop id; catalog lives in
//                engine/RoomBackdrop.js — code is the catalog truth)
//     unlocked : { [id]: { source, ts } }   ← APPEND-ONLY
//
// Laws honored:
//   • Spend path is the canonical atomic spendCoins transaction —
//     no read-then-write, no double-spend, Terror writes DC only.
//   • Unlock is a transaction-MERGE (current || grant): append-only,
//     provenance-stamped, never overwrites an existing unlock.
//   • GRANT-BEFORE-FLAG: the unlock IS the grant; `equipped` is a
//     mere pointer written separately — a failed equip can never
//     burn a purchase, and re-calling unlock on an owned backdrop
//     is a no-op that never re-charges.
// ─────────────────────────────────────────────────────────────

export const unlockBackdrop = async (uid, backdropId, cost = 0, source = "atelier_purchase") => {
  uid = _uidKey(uid, "unlockBackdrop");
  if (!uid || !backdropId) return { ok: false, reason: "bad-args" };

  const ownRef = ref(db, `profiles/${uid}/roomBackdrops/unlocked/${backdropId}`);

  // Already owned? Never charge twice.
  const snap = await get(ownRef);
  if (snap.exists()) return { ok: true, reason: "already-owned" };

  // Charge — atomic check-and-debit; aborts untouched if short.
  if (cost > 0) {
    const paid = await spendCoins(uid, cost);
    if (!paid) return { ok: false, reason: "insufficient-coins" };
  }

  // Grant — append-only merge; preserves any concurrent grant's provenance.
  await runTransaction(ownRef, (current) => current || { source, ts: Date.now() });
  return { ok: true, reason: "purchased" };
};

export const equipBackdrop = async (uid, backdropId) => {
  uid = _uidKey(uid, "equipBackdrop");
  if (!uid || !backdropId) return false;
  await set(ref(db, `profiles/${uid}/roomBackdrops/equipped`), backdropId);
  return true;
};

// ─────────────────────────────────────────────────────────────
// DRAW PULSES — the Luxmoire/Grimoire cue economy (July 28, 2026)
// ─────────────────────────────────────────────────────────────
// Schema (NEW node — add to the hardening-session whitelist):
//   profiles/{uid}/drawPulse/
//     equipped : string (pulse id; catalog lives in DrawPulse.js —
//                code is the catalog truth, same as backdrops)
//     unlocked : { [id]: { source, ts } }   ← APPEND-ONLY
//
// Byte-for-byte the same shape and the same laws as roomBackdrops
// above, deliberately: this is a TERROR-LOCAL board cosmetic, not
// a 7th entry in the locked 6-slot IE cosmetic schema. A Luxmoire
// does not exist in Pierre or Snake Eyes, so there is nothing for
// an equip-anywhere slot to mean over there. Nothing to propagate.
//
// Per IE_FIREBASE_LAW §1 a NEW top-level profile field needs NO
// rules deploy — the hard $extra lock lives only on
// collection/$cardId; every other player-level field is
// permitted-but-unvalidated by design (the same finding that
// cleared worldMap and H28's gshift).
//
// Laws honored — identical to backdrops:
//   • Spend is the canonical atomic spendCoins transaction. Terror
//     writes DC only; no read-then-write, no double-spend.
//   • Unlock is a transaction-MERGE (current || grant): append-only,
//     provenance-stamped, never overwrites.
//   • GRANT-BEFORE-FLAG: the unlock IS the grant; `equipped` is a
//     pointer written separately, so a failed equip can never burn
//     a purchase and re-unlocking an owned pulse never re-charges.
// ─────────────────────────────────────────────────────────────

export const unlockDrawPulse = async (uid, pulseId, cost = 0, source = "emporium_signature") => {
  uid = _uidKey(uid, "unlockDrawPulse");
  if (!uid || !pulseId) return { ok: false, reason: "bad-args" };

  const ownRef = ref(db, `profiles/${uid}/drawPulse/unlocked/${pulseId}`);

  // Already owned? Never charge twice.
  const snap = await get(ownRef);
  if (snap.exists()) return { ok: true, reason: "already-owned" };

  // Charge — atomic check-and-debit; aborts untouched if short.
  if (cost > 0) {
    const paid = await spendCoins(uid, cost);
    if (!paid) return { ok: false, reason: "insufficient-coins" };
  }

  // Grant — append-only merge; preserves any concurrent grant's provenance.
  await runTransaction(ownRef, (current) => current || { source, ts: Date.now() });
  return { ok: true, reason: "purchased" };
};

export const equipDrawPulse = async (uid, pulseId) => {
  uid = _uidKey(uid, "equipDrawPulse");
  if (!uid || !pulseId) return false;
  await set(ref(db, `profiles/${uid}/drawPulse/equipped`), pulseId);
  return true;
};

// ─────────────────────────────────────────────────────────────
// COIN SKINS — T11 (2026-08-18)
// ─────────────────────────────────────────────────────────────
// Schema (NEW node — add to the hardening-session whitelist):
//   profiles/{uid}/coinSkin/
//     equipped : string (skin id; the catalog lives in coinSkins.js —
//                code is the catalog truth, same as backdrops/pulses)
//     unlocked : { [id]: { source, ts } }   ← APPEND-ONLY
//
// The third instance of the same eight lines, and that is the point:
// backdrops, draw pulses and coin skins are one shape wearing three
// names. A fourth Terror-local cosmetic should be a copy of this block
// and nothing more — the day it needs to be something else is the day
// this becomes a helper, not before.
//
// Laws honored, identical to both siblings:
//   • Spend is the canonical atomic spendCoins transaction.
//   • Unlock is a transaction-MERGE (current || grant): append-only,
//     provenance-stamped, never overwrites.
//   • GRANT-BEFORE-FLAG: the unlock IS the grant; `equipped` is a
//     pointer written separately, so a failed equip can never burn a
//     purchase and re-unlocking an owned skin never re-charges.
//
// ⚠ A SKIN NEVER TOUCHES THE OUTCOME. The Solrend flip is decided from
// match.createdAt parity long before any of this is read. Nothing in
// this block, or in coinSkins.js, can bend a duel — see the law at the
// top of coinSkins.js.
// ─────────────────────────────────────────────────────────────

// ◆ TWO LANES (2026-08-18, the endgame ruling). `currency` is "CC" or
// "DC" and comes from the catalog row, NEVER from the caller's own
// idea of it — a client that hands you a price and a currency is
// describing what it would like to pay. coinSkins.currencyOf() is the
// one place that answers this, and the shelf and this function both
// ask it rather than agreeing by luck.
//
// One-per-account falls out for free: the unlock node is append-only
// and an owned skin returns before the debit, so a ◆ mythic can never
// be bought twice however many tabs are open.
export const unlockCoinSkin = async (uid, skinId, cost = 0, source = "emporium_coin", currency = "CC") => {
  uid = _uidKey(uid, "unlockCoinSkin");
  if (!uid || !skinId) return { ok: false, reason: "bad-args" };

  const ownRef = ref(db, `profiles/${uid}/coinSkin/unlocked/${skinId}`);

  // Already owned? Never charge twice.
  const snap = await get(ownRef);
  if (snap.exists()) return { ok: true, reason: "already-owned" };

  // Charge — atomic check-and-debit in the right lane; aborts untouched
  // if short. GRANT-BEFORE-FLAG still holds: the unlock below IS the
  // grant, and `equipped` is written separately.
  if (cost > 0) {
    const paid = currency === "DC"
      ? await spendDivineCrests(uid, cost)
      : await spendCoins(uid, cost);
    if (!paid) return { ok: false, reason: currency === "DC" ? "insufficient-crests" : "insufficient-coins" };
  }

  // Grant — append-only merge; preserves any concurrent grant's
  // provenance. `paid` records the lane, so a future audit can tell a
  // ◆ mythic from a ◕ fable without re-reading the catalog.
  await runTransaction(ownRef, (current) => current || { source, ts: Date.now(), currency, paid: cost });
  return { ok: true, reason: "purchased" };
};

export const equipCoinSkin = async (uid, skinId) => {
  uid = _uidKey(uid, "equipCoinSkin");
  if (!uid || !skinId) return false;
  await set(ref(db, `profiles/${uid}/coinSkin/equipped`), skinId);
  return true;
};
